"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import GoogleConnectLink from "@/components/GoogleConnectLink";
import { getSettings } from "@/lib/settings-api";
import {
  completeScheduleBlock,
  createScheduleBlock,
  deleteScheduleBlock,
  getSchedule,
  judgeGoalStep,
  listGoals,
  createGoal,
  getPlanningOverview,
  replaceGoalMilestones,
  undoTaskCompletion,
  updateScheduleBlock,
} from "@/lib/api";
import type {
  ScheduleBlock,
  GoalItem,
  AutoPlanResponse,
  PlanningOverview,
  CommitmentLevel,
  GoalDriftGoal,
  MilestoneInput,
  MilestoneSubStepInput,
} from "@/lib/api";
import { applyPlanDiff, generatePlanDiff } from "@/lib/api/plan";
import type {
  PlanDiffEntry,
  PlanDiffResult,
  PlanDraftBlock,
} from "@/lib/api/projection-types";
import {
  useCarryForwardContext,
  useDataRefresh,
  useGoalFocusContext,
  type CarryForwardContextSummary,
  type CarryForwardPlannedFocus,
  type GoalFocusTarget,
} from "@/lib/store";
import {
  buildCarryForwardFocusHint,
  buildCarryForwardMessage,
  buildExecutionPatternNextStep,
  getCarryForwardFocusBadgeLabel,
  matchesCarryForwardFocusTitle,
  hasScheduleCarryForwardAction,
} from "@/lib/carryForwardSummary";
import TimeBlock from "./TimeBlock";
import RetroactiveChecklist from "./RetroactiveChecklist";
import AIPlanDiffSheet from "@/components/plan/AIPlanDiffSheet";
import BlockEditModal, { type BlockEditPayload } from "@/components/plan/BlockEditModal";
import PlanBoard from "@/components/plan/PlanBoard";
import PlanRightRail from "@/components/plan/PlanRightRail";
import StepBreakdownDialog, {
  type StepBreakdownProposal,
} from "@/components/plan/StepBreakdownDialog";
import { getCommitmentLabel, WORK_GROUP_LABEL } from "@/lib/commitmentLabels";
import { goalWeightLabel } from "@/lib/goalWeights";
import {
  BLOCK_KIND_LABEL,
  LIFE_AREA_COLORS as SHARED_LIFE_AREA_COLORS,
  LIFE_AREA_META,
  NEUTRAL_BLOCK_COLOR,
  NEUTRAL_KINDS,
  UNLINKED_AREA,
  UNLINKED_LABEL,
} from "@/lib/lifeAreas";
import { buildPlanSupportGoals, type PlanSupportGoalReason } from "@/lib/planSupportGoals";
import { displaySavedResourceBlockTitle, sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";
import { formatLocalDateInput, parseUTCishDate } from "@/lib/utils";
import {
  useCrossSurfaceConfirmationStore,
  type CrossSurfaceConfirmationTruth,
} from "@/lib/stores/crossSurfaceConfirmation";
import { useChatRevealHighlight } from "@/lib/useChatRevealHighlight";
import { useChatRevealStore } from "@/lib/stores/chatReveal";
import { pickInitialPlanDate } from "@/lib/reveal/pickInitialPlanDate";

// Defaults used until the user's saved Working Hours setting loads.
// Once settings come back, the component swaps to the user-chosen
// `day_start_hour` / `day_end_hour`. The timeline now respects
// whatever window the user picked under Settings → Working Hours.
const DEFAULT_TIMELINE_START_HOUR = 7;
const DEFAULT_TIMELINE_END_HOUR = 21;
const PX_PER_MINUTE = 1.15;
const DRAG_SNAP_MINUTES = 5;
const MAX_VISIBLE_LATER_NOTES = 2;
const BOARD_NOTE_LEFT_PX = 80;
const BOARD_NOTE_RIGHT_PX = 8;
const BOARD_NOTE_GUTTER_PX = 12;
const AI_PLAN_EXPECTED_WAIT_COPY =
  "AI is planning. This usually takes 30-90 seconds on the deployed site. You can keep editing or cancel.";
const AI_PLAN_LONG_WAIT_COPY =
  "Still planning. Render may still be working after a cold start, and you can keep editing or cancel.";

function makePlanApplyIdempotencyKey(draftId: string | null | undefined, date: string) {
  const random =
    typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `plan-apply:${draftId || date}:${random}`;
}

function buildPlanConfirmationTruth(
  targetId: string | null | undefined,
  owningService: string
): CrossSurfaceConfirmationTruth | null {
  if (!targetId) return null;
  return {
    targetKind: "schedule_block",
    targetId,
    destinationLayer: "plan",
    owningService,
  };
}

// Mirror of backend `_first_uncompleted_milestone_title` in
// goal_drift.py — returns the smallest schedulable next-step
// title for a goal: the first uncompleted sub_step if the parent
// milestone has been broken down (Phase 4), otherwise the parent
// milestone title itself. Used everywhere we send next_step into
// AI Time Box preferences so the planner schedules the smallest
// concrete unit, not the un-broken-down parent.
function firstUncompletedSchedulableTitle(
  milestones:
    | Array<{
        title: string;
        completed: boolean;
        sub_steps?: Array<{ title: string; completed: boolean }>;
      }>
    | undefined
    | null,
): string | null {
  if (!milestones) return null;
  for (const m of milestones) {
    if (m.completed) continue;
    if (m.sub_steps && m.sub_steps.length > 0) {
      for (const s of m.sub_steps) {
        if (!s.completed && s.title?.trim()) return s.title;
      }
      continue;
    }
    if (m.title?.trim()) return m.title;
  }
  return null;
}

// Canonical palette — see frontend/lib/lifeAreas.ts. Re-exported under the
// existing name so call sites keep working without a full rename.
const LIFE_AREA_COLOR_VALUES: Record<string, string> = SHARED_LIFE_AREA_COLORS;

const DEFAULT_LIFE_AREA = "personal";
const DEFAULT_COMMITMENT_LEVEL: CommitmentLevel = "planned";

const TIMEBOX_PRESETS: Array<{
  id: string;
  title: string;
  lifeArea: keyof typeof LIFE_AREA_META;
  duration: number;
}> = [
  { id: "working", title: "Work block", lifeArea: "career", duration: 60 },
  { id: "deep-learning", title: "Deep learning", lifeArea: "learning", duration: 45 },
  { id: "habit", title: "Habit block", lifeArea: "health", duration: 30 },
  { id: "personal", title: "Personal reset", lifeArea: "personal", duration: 30 },
];

function todayStr(): string {
  return formatLocalDateInput(new Date());
}

function normalizeLifeAreaKey(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!normalized) return null;
  if (normalized in LIFE_AREA_META) return normalized;

  switch (normalized) {
    case "relationship":
      return "relationships";
    case "well_being":
    case "wellbeing":
    case "wellness":
      // "Wellness" is no longer a canonical life area — collapse it
      // into "personal" so legacy data still renders consistently.
      return "personal";
    case "fitness":
      return "health";
    case "work":
      return "career";
    default:
      return null;
  }
}

function normalizeLookupTitle(value: string | null | undefined): string | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  return normalized || null;
}

type LifeAreaLookup = {
  byGoalId: Record<string, string>;
  byGoalTitle: Record<string, string>;
};

function buildLifeAreaLookup(goals: GoalItem[]): LifeAreaLookup {
  const byGoalId: Record<string, string> = {};
  const byGoalTitle: Record<string, string> = {};

  for (const goal of goals) {
    const area = normalizeLifeAreaKey(goal.category);
    if (!area) continue;
    byGoalId[goal.id] = area;
    const titleKey = normalizeLookupTitle(goal.title);
    if (titleKey) {
      byGoalTitle[titleKey] = area;
    }
  }

  return { byGoalId, byGoalTitle };
}

function resolveLifeAreaFromLookup(
  goalId: string | null | undefined,
  goalTitle: string | null | undefined,
  lookup?: LifeAreaLookup
): string | null {
  if (!lookup) return null;
  if (goalId && lookup.byGoalId[goalId]) return lookup.byGoalId[goalId];
  const titleKey = normalizeLookupTitle(goalTitle);
  return titleKey ? lookup.byGoalTitle[titleKey] ?? null : null;
}

function resolveBlockMetadataLifeArea(blockMetadata?: Record<string, unknown> | null): string | null {
  if (!blockMetadata) return null;
  return normalizeLifeAreaKey(
    blockMetadata.life_area ??
      blockMetadata.lifeArea ??
      blockMetadata.goal_category ??
      blockMetadata.goalCategory ??
      blockMetadata.category ??
      blockMetadata.area
  );
}

function shiftDate(date: string, amount: number): string {
  const value = new Date(`${date}T12:00:00`);
  value.setDate(value.getDate() + amount);
  return formatLocalDateInput(value);
}

function buildDayStrip(date: string) {
  return Array.from({ length: 7 }, (_, index) => {
    const offset = index - 3;
    const value = shiftDate(date, offset);
    const parsed = new Date(`${value}T12:00:00`);
    return {
      value,
      label: parsed.toLocaleDateString("en-US", { weekday: "short" }),
      day: parsed.getDate(),
    };
  });
}

function minuteOffset(iso: string, timelineStartHour: number): number {
  const date = parseUTCishDate(iso);
  const totalMinutes = date.getHours() * 60 + date.getMinutes();
  return Math.max(0, totalMinutes - timelineStartHour * 60);
}

function blockDurationMinutes(start: string, end: string): number {
  return Math.max(15, Math.round((parseUTCishDate(end).getTime() - parseUTCishDate(start).getTime()) / 60000));
}

interface BoardNoteLayout {
  column: number;
  count: number;
}

function computeBoardLayouts(
  blocks: Array<{ id: string; startMs: number; endMs: number }>
): Record<string, BoardNoteLayout> {
  const sorted = [...blocks].sort((a, b) => {
    if (a.startMs !== b.startMs) return a.startMs - b.startMs;
    return a.endMs - b.endMs;
  });
  const layouts: Record<string, BoardNoteLayout> = {};
  let active: Array<{ id: string; endMs: number; column: number }> = [];
  let clusterIds: string[] = [];
  let clusterMax = 1;

  const finalizeCluster = () => {
    for (const id of clusterIds) {
      layouts[id] = { ...layouts[id], count: clusterMax };
    }
    clusterIds = [];
    clusterMax = 1;
  };

  for (const block of sorted) {
    active = active.filter((item) => item.endMs > block.startMs);
    if (active.length === 0 && clusterIds.length > 0) {
      finalizeCluster();
    }

    const usedColumns = new Set(active.map((item) => item.column));
    let column = 0;
    while (usedColumns.has(column)) column += 1;

    layouts[block.id] = { column, count: 1 };
    active.push({ id: block.id, endMs: block.endMs, column });
    clusterIds.push(block.id);
    clusterMax = Math.max(clusterMax, active.length);
  }

  if (clusterIds.length > 0) {
    finalizeCluster();
  }

  return layouts;
}

function blockLifeArea(block: {
  goal_id?: string | null;
  goal_title?: string | null;
  goal_category?: string | null;
  life_area?: string | null;
  color?: string | null;
  block_metadata?: Record<string, unknown> | null;
}, lookup?: LifeAreaLookup): string | null {
  // Order matters: user-edited `life_area` is authoritative. Falling
  // back to `block_metadata.life_area` first caused stale AI-inferred
  // metadata to win after a manual edit, so the timeline tile kept
  // the old area's rail color even though the block itself was saved
  // with the new area.
  const lifeArea = normalizeLifeAreaKey(block.life_area);
  if (lifeArea) return lifeArea;
  const metadataArea = resolveBlockMetadataLifeArea(block.block_metadata);
  if (metadataArea) return metadataArea;
  const goalCategory = normalizeLifeAreaKey(block.goal_category);
  if (goalCategory) return goalCategory;
  return resolveLifeAreaFromLookup(block.goal_id, block.goal_title, lookup);
}

function resolvedBlockColorValue(block: {
  goal_id?: string | null;
  goal_title?: string | null;
  goal_category?: string | null;
  life_area?: string | null;
  color?: string | null;
  block_metadata?: Record<string, unknown> | null;
}, lookup?: LifeAreaLookup): string | null {
  const area = blockLifeArea(block, lookup);
  if (area && LIFE_AREA_COLOR_VALUES[area]) return LIFE_AREA_COLOR_VALUES[area];
  return block.color ?? null;
}

function hydrateScheduleBlock(block: ScheduleBlock, lookup?: LifeAreaLookup): ScheduleBlock {
  const resolvedArea = blockLifeArea(block, lookup);
  if (!resolvedArea) {
    return block;
  }

  // When the block has an explicit life_area (user edit or backend
  // assignment), refresh the cached `color` and `block_metadata.life_area`
  // so they don't outlive their source. Otherwise we'd retain stale
  // values across area changes.
  const explicitLifeArea = normalizeLifeAreaKey(block.life_area);
  const colorForArea = LIFE_AREA_COLOR_VALUES[resolvedArea] ?? null;

  return {
    ...block,
    goal_category: normalizeLifeAreaKey(block.goal_category) ?? resolvedArea,
    life_area: explicitLifeArea ?? resolvedArea,
    color: explicitLifeArea
      ? colorForArea ?? block.color
      : block.color ?? colorForArea,
    block_metadata: {
      ...(block.block_metadata ?? {}),
      life_area: explicitLifeArea
        ? resolvedArea
        : resolveBlockMetadataLifeArea(block.block_metadata) ??
          (block.block_metadata?.life_area as string | undefined) ??
          resolvedArea,
    },
  };
}

function hydrateScheduleBlocks(blocks: ScheduleBlock[], lookup?: LifeAreaLookup): ScheduleBlock[] {
  return blocks.map((block) => hydrateScheduleBlock(block, lookup));
}

// U27 — fallback bridge: if planning_overview returned Google Calendar
// events that aren't yet in the persisted schedule blocks (race with
// backend sync, transient sync failure, or token refresh in flight),
// surface them as virtual `kind="calendar"` entries so the timeline
// always shows real meetings even when the sync layer hasn't caught
// up yet. Dedup is by external_event_id stored in block_metadata —
// when the backend sync (calendar_sync.py) eventually wins the race,
// the virtual entry quietly drops.
function bridgeCalendarEventsIntoBlocks(
  blocks: ScheduleBlock[],
  calendarEvents: Array<{
    id: string;
    title: string;
    start?: string | null;
    end?: string | null;
    is_all_day?: boolean;
  }>,
): ScheduleBlock[] {
  if (!calendarEvents.length) return blocks;
  const persistedExternalIds = new Set(
    blocks
      .filter((b) => b.kind === "calendar")
      .map((b) => {
        const meta = (b.block_metadata ?? {}) as { external_event_id?: string };
        return meta.external_event_id;
      })
      .filter((value): value is string => Boolean(value)),
  );
  const additions: ScheduleBlock[] = [];
  for (const event of calendarEvents) {
    if (!event.id || persistedExternalIds.has(event.id)) continue;
    if (!event.start || !event.end) continue;
    additions.push({
      id: `bridge:${event.id}`,
      kind: "calendar",
      title: event.title || "Calendar event",
      description: null,
      start_time: event.start,
      end_time: event.end,
      commitment_level: "planned",
      task_id: null,
      goal_id: null,
      goal_title: null,
      goal_category: null,
      life_area: null,
      rationale: "Imported from Google Calendar",
      color: "#4F8FCB",
      is_completed: false,
      is_ai_generated: false,
      schedule_date: event.start,
      created_at: event.start,
      block_metadata: {
        external_source: "google_calendar",
        external_event_id: event.id,
        is_all_day: Boolean(event.is_all_day),
        bridged: true,
      },
    });
  }
  if (!additions.length) return blocks;
  return [...blocks, ...additions].sort(
    (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
  );
}

function hydratePlanDraftBlock(block: PlanDraftBlock, lookup?: LifeAreaLookup): PlanDraftBlock {
  const resolvedArea = blockLifeArea(block, lookup);
  if (!resolvedArea) {
    return block;
  }

  return {
    ...block,
    goal_category: normalizeLifeAreaKey(block.goal_category) ?? resolvedArea,
    life_area: normalizeLifeAreaKey(block.life_area) ?? resolvedArea,
    color: block.color ?? LIFE_AREA_COLOR_VALUES[resolvedArea] ?? block.color,
    block_metadata: {
      ...(block.block_metadata ?? {}),
      life_area:
        resolveBlockMetadataLifeArea(block.block_metadata) ??
        (block.block_metadata?.life_area as string | undefined) ??
        resolvedArea,
    },
  };
}

function planDraftBlockToScheduleBlock(block: PlanDraftBlock, lookup?: LifeAreaLookup): ScheduleBlock {
  const hydrated = hydratePlanDraftBlock(block, lookup);
  return {
    ...hydrated,
    color:
      hydrated.color ??
      resolvedBlockColorValue(hydrated, lookup) ??
      LIFE_AREA_COLOR_VALUES[DEFAULT_LIFE_AREA],
  };
}

function hydratePlanDiffResult(result: PlanDiffResult, lookup?: LifeAreaLookup): PlanDiffResult {
  if (result.status !== "ready") return result;

  const hydratedBlocks = result.blocks.map((block) => hydratePlanDraftBlock(block, lookup));
  const blockById = new Map(hydratedBlocks.map((block) => [block.id, block] as const));
  const syncEntries = (entries: PlanDiffEntry[]) =>
    entries.map((entry) => {
      const matchedBlock = blockById.get(entry.current.id);
      if (!matchedBlock) return entry;
      return {
        ...entry,
        current: {
          ...entry.current,
          goal_category:
            normalizeLifeAreaKey(entry.current.goal_category) ??
            matchedBlock.life_area ??
            matchedBlock.goal_category ??
            entry.current.goal_category,
        },
      };
    });

  return {
    ...result,
    blocks: hydratedBlocks,
    adds: syncEntries(result.adds),
    moves: syncEntries(result.moves),
    untouched: syncEntries(result.untouched),
  };
}

function boardColumnWidthCss(columnCount: number): string {
  if (columnCount <= 1) {
    return `calc(100% - ${BOARD_NOTE_LEFT_PX + BOARD_NOTE_RIGHT_PX}px)`;
  }

  return `calc((100% - ${BOARD_NOTE_LEFT_PX + BOARD_NOTE_RIGHT_PX}px - ${(columnCount - 1) * BOARD_NOTE_GUTTER_PX}px) / ${columnCount})`;
}

function boardColumnLeftCss(column: number, columnCount: number): string {
  if (columnCount <= 1) {
    return `${BOARD_NOTE_LEFT_PX}px`;
  }

  return `calc(${BOARD_NOTE_LEFT_PX}px + (${column} * ((100% - ${BOARD_NOTE_LEFT_PX + BOARD_NOTE_RIGHT_PX}px - ${(columnCount - 1) * BOARD_NOTE_GUTTER_PX}px) / ${columnCount})) + ${column * BOARD_NOTE_GUTTER_PX}px)`;
}

type BoardMetricBlock = {
  id?: string;
  kind: string;
  start_time: string;
  end_time: string;
  goal_id?: string | null;
  goal_title?: string | null;
  goal_category?: string | null;
  life_area?: string | null;
  color?: string | null;
  block_metadata?: Record<string, unknown> | null;
  is_ai_generated?: boolean;
};

function summarizeBoardMetricBlocks(
  blocks: BoardMetricBlock[],
  lookup?: LifeAreaLookup
) {
  const breakdown = new Map<string, { area: string; label: string; minutes: number; block_count: number; color: string }>();
  let totalFocusMinutes = 0;
  let totalBreakMinutes = 0;

  for (const block of blocks) {
    const minutes = blockDurationMinutes(block.start_time, block.end_time);
    if (block.kind === "break" || block.kind === "buffer") {
      totalBreakMinutes += minutes;
      continue;
    }

    const resolvedArea = blockLifeArea(block, lookup);
    const shouldCountAsLifeAreaTime =
      block.kind === "task" ||
      block.kind === "custom" ||
      (block.kind === "calendar" && Boolean(resolvedArea));

    if (!shouldCountAsLifeAreaTime) {
      continue;
    }

    totalFocusMinutes += minutes;
    const areaKey = resolvedArea ?? UNLINKED_AREA;
    const meta = LIFE_AREA_META[areaKey];
    const label = meta?.label ?? (areaKey === UNLINKED_AREA ? UNLINKED_LABEL : areaKey);
    const color =
      LIFE_AREA_COLOR_VALUES[areaKey] ??
      (areaKey === UNLINKED_AREA ? NEUTRAL_BLOCK_COLOR : NEUTRAL_BLOCK_COLOR);

    const existing = breakdown.get(areaKey);
    if (existing) {
      existing.minutes += minutes;
      existing.block_count += 1;
      continue;
    }

    breakdown.set(areaKey, {
      area: areaKey,
      label,
      minutes,
      block_count: 1,
      color,
    });
  }

  return {
    totalFocusMinutes,
    totalBreakMinutes,
    lifeAreaBreakdown: Array.from(breakdown.values()).sort((a, b) => b.minutes - a.minutes),
  };
}

function buildAiPlanPreviewBoardBlocks(
  plan: PlanDiffResult,
  currentBlocks: ScheduleBlock[],
  lookup?: LifeAreaLookup
): ScheduleBlock[] {
  if (plan.status !== "ready") return [];

  const previewBlocks = plan.blocks.map((block) => planDraftBlockToScheduleBlock(block, lookup));
  const previewIds = new Set(previewBlocks.map((block) => block.id));
  const replacedCurrentIds = new Set<string>();

  for (const entry of [...plan.moves, ...plan.untouched]) {
    if (previewIds.has(entry.current.id) && entry.previous?.id) {
      replacedCurrentIds.add(entry.previous.id);
    }
  }

  return [
    ...currentBlocks.filter((block) => !block.is_ai_generated && !replacedCurrentIds.has(block.id)),
    ...previewBlocks,
  ].sort((a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime());
}

function deriveAiPlanBoardTruth(
  plan: PlanDiffResult,
  currentBlocks: ScheduleBlock[],
  lookup?: LifeAreaLookup
): PlanDiffResult {
  const hydratedPlan = hydratePlanDiffResult(plan, lookup);
  if (hydratedPlan.status !== "ready") return hydratedPlan;

  const previewBoardBlocks = buildAiPlanPreviewBoardBlocks(hydratedPlan, currentBlocks, lookup);
  const boardBlockById = new Map(previewBoardBlocks.map((block) => [block.id, block] as const));
  const enrichEntriesFromBoard = (entries: PlanDiffEntry[]) =>
    entries.map((entry) => {
      const matchedBlock = boardBlockById.get(entry.current.id);
      if (!matchedBlock) return entry;
      const area = blockLifeArea(matchedBlock, lookup);
      const color = area ? LIFE_AREA_COLOR_VALUES[area] ?? matchedBlock.color ?? null : matchedBlock.color ?? null;
      return {
        ...entry,
        current: {
          ...entry.current,
          goal_category:
            normalizeLifeAreaKey(entry.current.goal_category) ??
            area ??
            entry.current.goal_category,
          life_area: area ?? entry.current.life_area ?? null,
          color: color ?? entry.current.color ?? null,
        },
      };
    });
  const nextMetrics = summarizeBoardMetricBlocks(previewBoardBlocks, lookup);

  return {
    ...hydratedPlan,
    total_focus_minutes: nextMetrics.totalFocusMinutes,
    total_break_minutes: nextMetrics.totalBreakMinutes,
    life_area_breakdown: nextMetrics.lifeAreaBreakdown,
    adds: enrichEntriesFromBoard(hydratedPlan.adds),
    moves: enrichEntriesFromBoard(hydratedPlan.moves),
    untouched: enrichEntriesFromBoard(hydratedPlan.untouched),
  };
}

function blockCommitmentClasses(block: ScheduleBlock | null | undefined): string {
  if (!block) return "";
  if ((block.priority ?? 0) >= 2) return "";
  if (block.commitment_level === "must_do") {
    return "shadow-[inset_0_0_0_2px_#B8893B,0_0_10px_rgba(184,137,59,0.18)]";
  }
  if (block.commitment_level === "flexible") {
    // Dashed border still signals "flexible commitment" semantically, but
    // we drop the 0.85 opacity so the block doesn't visually read as
    // un-editable. Click + drag have always been allowed for flexible
    // blocks; the previous fade just made that hard to discover.
    return "border-dashed";
  }
  return "";
}

// Rhythm blocks (routine / recovery) stay visually neutral by default
// so the Timeline reads as calm. But once the user explicitly labels
// one with a life area in the editor, their intent wins — we'd rather
// color a single rhythm block with its assigned area than ignore the
// edit and keep it grey.
function isRhythmBlock(block: ScheduleBlock): boolean {
  if (!NEUTRAL_KINDS.has(block.kind)) return false;
  if (normalizeLifeAreaKey(block.life_area)) return false;
  if (resolveBlockMetadataLifeArea(block.block_metadata)) return false;
  return true;
}

function blockTone(block: ScheduleBlock) {
  if (block.is_completed) return "border-accent/22 bg-accent/8 text-accent";
  if (!isRhythmBlock(block)) {
    const area = LIFE_AREA_META[blockLifeArea(block) ?? ""];
    if (area) return area.tone;
  }
  return "border-border/60 bg-card text-text-light";
}

function blockAccent(_block: ScheduleBlock) {
  return "from-sage-pale/70 via-white to-white";
}

function hexWithAlpha(color: string, alphaHex: string): string {
  return /^#[0-9a-f]{6}$/i.test(color) ? `${color}${alphaHex}` : color;
}

function blockAreaColorValue(block: ScheduleBlock): string | null {
  if (!isRhythmBlock(block)) {
    const areaKey = blockLifeArea(block);
    if (areaKey) return LIFE_AREA_COLOR_VALUES[areaKey] ?? null;
  }
  return null;
}

function blockToneStyle(block: ScheduleBlock) {
  const color = blockAreaColorValue(block);
  if (!color) return undefined;
  return {
    borderColor: hexWithAlpha(color, "40"),
    backgroundColor: hexWithAlpha(color, "14"),
  };
}

function blockRailStyle(block: ScheduleBlock) {
  if (block.is_completed) return { backgroundColor: "var(--accent)" };
  const color = blockAreaColorValue(block);
  if (color) return { backgroundColor: color };
  return { backgroundColor: "var(--muted)" };
}

function blockKindLabel(block: ScheduleBlock) {
  if (block.is_completed) return "Done";
  if (block.kind !== "task") return BLOCK_KIND_LABEL[block.kind] ?? WORK_GROUP_LABEL;
  return WORK_GROUP_LABEL;
}

function blockBadgeTone(block: ScheduleBlock) {
  if (block.is_completed) return "bg-accent/12 text-accent";
  // Non-task kinds (calendar/buffer/break/custom) use a neutral chip so
  // kind reads as a text label, not a color.
  if (block.kind !== "task") return "bg-border/60 text-muted";
  const area = LIFE_AREA_META[blockLifeArea(block) ?? ""];
  if (area) return area.badge;
  return "bg-border/60 text-text-light";
}

function blockPrimaryBadge(block: ScheduleBlock) {
  if (block.kind === "calendar" || block.kind === "custom" || block.kind === "buffer" || block.kind === "break") {
    return {
      label: blockKindLabel(block),
      tone: blockBadgeTone(block),
    };
  }

  if (block.commitment_level === "must_do") {
    return {
      label: getCommitmentLabel(block.commitment_level),
      tone: "bg-rose/10 text-rose",
    };
  }

  if (block.commitment_level === "flexible") {
    return {
      label: getCommitmentLabel(block.commitment_level),
      tone: "bg-blue/10 text-blue",
    };
  }

  return {
    label: getCommitmentLabel(block.commitment_level),
    tone: "bg-border/70 text-text-light",
  };
}

function blockMeaningDetail(block: ScheduleBlock) {
  const isSavedResource = block.block_metadata?.source === "queue";
  if (block.is_completed) return "Finished";
  if (block.kind === "calendar") return "External";
  if (block.kind === "custom") return isSavedResource ? "Saved resource" : "Optional";
  if (block.kind === "buffer") return "Leave space";
  if (block.kind === "break") return "Reset";
  return "Important work";
}

function shouldShowMeaningDetail(block: ScheduleBlock, isSelected: boolean) {
  if (isSelected || block.is_completed) return true;
  return (
    block.kind === "calendar" ||
    block.kind === "custom" ||
    block.kind === "buffer" ||
    block.kind === "break"
  );
}

function paperTilt(block: ScheduleBlock) {
  const seed = Array.from(block.id).reduce((sum, char) => sum + char.charCodeAt(0), 0);
  const tilts = [-0.8, -0.35, 0, 0.45, 0.9];
  return tilts[seed % tilts.length];
}

function formatTimeRange(start: string, end: string): string {
  return `${parseUTCishDate(start).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} - ${parseUTCishDate(end).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

function formatContextTime(value?: string | null): string {
  if (!value) return "";
  return parseUTCishDate(value).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function compactGoalLabel(goalTitle: string): string {
  const cleaned = sanitizeSurfaceTitle(goalTitle);
  if (cleaned.length <= 28) return cleaned;
  return `${cleaned.slice(0, 28).trimEnd()}…`;
}

function compactGoalCopy(text?: string | null, maxChars: number = 120): string {
  const normalized = sanitizeSurfaceTitle((text || "").replace(/\s+/g, " ").trim());
  if (!normalized) return "Still needs a clearer next move and a lighter execution path.";
  const firstSentence = normalized.split(/(?<=[.!?])\s+/)[0] || normalized;
  if (firstSentence.length <= maxChars) return firstSentence;
  const clippedParts = firstSentence.slice(0, maxChars).split(" ");
  clippedParts.pop();
  const clipped = clippedParts.join(" ").trim();
  return `${clipped || firstSentence.slice(0, maxChars).trim()}…`;
}

function compactBoardRationale(text?: string | null, maxChars: number = 110): string {
  const normalized = (text || "").replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  if (normalized.length <= maxChars) return normalized;
  const clipped = normalized.slice(0, maxChars).split(" ");
  clipped.pop();
  return `${(clipped.join(" ").trim() || normalized.slice(0, maxChars).trim()).replace(/[ ,.;:]+$/, "")}…`;
}

function matchesGoalFocusTarget(
  block: Pick<ScheduleBlock, "goal_id" | "goal_title">,
  target: GoalFocusTarget | null
) {
  if (!target) return false;
  if (block.goal_id && block.goal_id === target.goalId) return true;
  if (block.goal_title && block.goal_title === target.goalTitle) return true;
  return false;
}

function formatTimelineMoment(
  minutesFromTimeline: number,
  timelineStartHour: number,
): string {
  const totalMinutes = timelineStartHour * 60 + Math.max(0, Math.round(minutesFromTimeline));
  const date = new Date();
  date.setHours(Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0);
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function compactSurfaceLabel(value: string, maxChars: number = 54): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxChars) return normalized;
  const clipped = normalized.slice(0, maxChars).split(" ");
  clipped.pop();
  return `${(clipped.join(" ").trim() || normalized.slice(0, maxChars).trim()).replace(/[ ,.;:]+$/, "")}…`;
}

function displayBlockTitle(block: ScheduleBlock): string {
  return displaySavedResourceBlockTitle(block);
}

function nextGoalMove(goal: GoalItem): string {
  const nextMilestone = goal.milestones.find((milestone) => !milestone.completed)?.title;
  if (nextMilestone) return nextMilestone;
  if (goal.description) return compactGoalCopy(goal.description, 72);
  return "Break this into a visible next step.";
}

function supportGoalBadge(reason: PlanSupportGoalReason) {
  if (reason === "focused") {
    return {
      label: "In focus",
      tone: "bg-forest-med text-white",
    };
  }
  if (reason === "recovery") {
    return {
      label: "Needs restart",
      tone: "bg-blue/10 text-blue",
    };
  }
  if (reason === "protected") {
    return {
      label: "Protect this week",
      tone: "bg-amber/15 text-amber",
    };
  }
  if (reason === "steady") {
    return {
      label: "Holding steady",
      tone: "bg-sage-pale text-forest-med",
    };
  }
  return {
    label: "Supporting context",
    tone: "bg-secondary text-text-light",
  };
}

function PlanSupportGoalCard({
  goal,
  reason,
}: {
  goal: GoalItem
  reason: PlanSupportGoalReason
}) {
  const meta = LIFE_AREA_META[goal.category || "personal"] || {
    emoji: "🎯",
    label: goal.category || "Personal",
  }
  const badge = supportGoalBadge(reason)

  return (
    <div
      data-testid={`plan-support-goal-${goal.id}`}
      className="rounded-[24px] border border-border/70 bg-bg px-3.5 py-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-text">
              {meta.emoji} {sanitizeSurfaceTitle(goal.title)}
            </p>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.tone}`}>
              {badge.label}
            </span>
          </div>
          <p className="mt-1 text-[11px] leading-relaxed text-muted">
            {compactGoalCopy(goal.description, 92)}
          </p>
        </div>
        <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold text-text-light shadow-sm">
          {Math.round(goal.progress_pct * 100)}%
        </span>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold text-text-light shadow-sm">
          {meta.label}
        </span>
        {goal.target_date && (
          <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold text-text-light shadow-sm">
            {new Date(goal.target_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </span>
        )}
      </div>

      <div className="mt-2.5 rounded-2xl border border-border/70 bg-white/80 px-3 py-2.5">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
          Best next move
        </p>
        <p className="mt-1 text-[11px] leading-relaxed text-text-light">
          {nextGoalMove(goal)}
        </p>
      </div>
    </div>
  )
}

interface Props {
  onNavigate: (tab: string) => void;
  autoOpenPlanToken?: number;
}

type BoardInteractionMode = "move" | "resize";

interface BoardInteractionState {
  blockId: string;
  mode: BoardInteractionMode;
  offsetY: number;
  currentTop: number;
  currentDuration: number;
  originalTop: number;
  originalDuration: number;
  conflictTitle: string | null;
}

interface PendingBoardMoveState {
  blockId: string;
  startClientX: number;
  startClientY: number;
  offsetY: number;
  originalTop: number;
  originalDuration: number;
}

interface LaterBoardSummary {
  count: number;
  startTime: string;
  endTime: string;
}

interface ScheduleUndoNotice {
  taskId: string;
  blockId: string;
  title: string;
  carryForwardSummarySnapshot?: CarryForwardContextSummary | null;
  plannedFocusSnapshot?: CarryForwardPlannedFocus | null;
  goalFocusSnapshot?: GoalFocusTarget | null;
}

export default function ScheduleGoalsScreen({
  onNavigate,
  autoOpenPlanToken = 0,
}: Props) {
  // CLCD: when this screen mounts because the user clicked "View in
  // Plan" from a Reflect AI suggestion, the chatReveal store carries
  // the target date BEFORE this component renders. Initialize `date`
  // from it so the first render already shows the right day. Logic is
  // in `pickInitialPlanDate` (pure helper) so it can be unit-tested
  // without mounting React. See lib/reveal/pickInitialPlanDate.ts for
  // why this is useState lazy init and NOT a useEffect.
  const [date, setDate] = useState(() =>
    pickInitialPlanDate(
      useChatRevealStore.getState().active,
      todayStr(),
    ),
  );
  const [blocks, setBlocks] = useState<ScheduleBlock[]>([]);
  const [goals, setGoals] = useState<GoalItem[]>([]);
  // User's saved Working Hours window — the BASELINE for the
  // timeline. The actual rendered range (timelineStartHour /
  // timelineEndHour below) auto-expands beyond this when any block
  // for the current day starts before or ends after the baseline,
  // so existing work is always visible. The baseline acts as a
  // floor; the displayed window is always a superset.
  const [userDayStartHour, setUserDayStartHour] = useState(
    DEFAULT_TIMELINE_START_HOUR,
  );
  const [userDayEndHour, setUserDayEndHour] = useState(
    DEFAULT_TIMELINE_END_HOUR,
  );

  useEffect(() => {
    let cancelled = false;
    void getSettings()
      .then((settings) => {
        if (cancelled) return;
        const start = Number(settings.day_start_hour);
        const end = Number(settings.day_end_hour);
        if (Number.isFinite(start) && start >= 0 && start <= 23) {
          setUserDayStartHour(start);
        }
        if (Number.isFinite(end) && end > start && end <= 24) {
          setUserDayEndHour(end);
        }
      })
      .catch(() => {
        // Network / auth hiccup — keep the defaults instead of
        // breaking the timeline render.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-expanding timeline window: starts from user setting, then
  // extends earlier / later as needed so every block on the day is
  // visible. Calendar imports are skipped because they can be
  // all-day events that would force the timeline to 00:00–24:00.
  // Industrial-standard behavior — the user's preference defines
  // the *default* viewport, but real work always wins.
  const timelineStartHour = useMemo(() => {
    let earliest = userDayStartHour;
    for (const block of blocks) {
      if (block.kind === "calendar") continue;
      const start = parseUTCishDate(block.start_time);
      const startHour = start.getHours();
      if (startHour < earliest) earliest = startHour;
    }
    return Math.max(0, earliest);
  }, [blocks, userDayStartHour]);
  const timelineEndHour = useMemo(() => {
    let latest = userDayEndHour;
    for (const block of blocks) {
      if (block.kind === "calendar") continue;
      const end = parseUTCishDate(block.end_time);
      let endHour = end.getHours();
      if (end.getMinutes() > 0) endHour += 1;
      if (endHour > latest) latest = endHour;
    }
    return Math.min(24, latest);
  }, [blocks, userDayEndHour]);
  const totalTimelineMinutes = useMemo(
    () => Math.max(60, (timelineEndHour - timelineStartHour) * 60),
    [timelineEndHour, timelineStartHour],
  );

  const [planningOverview, setPlanningOverview] = useState<PlanningOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAllBlocks, setShowAllBlocks] = useState(false);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [newGoalTitle, setNewGoalTitle] = useState("");
  const [creating, setCreating] = useState(false);
  const [retroGoalId, setRetroGoalId] = useState<string | null>(null);
  const [aiPlan, setAiPlan] = useState<PlanDiffResult | null>(null);
  const [aiPlanLoading, setAiPlanLoading] = useState(false);
  const [aiPlanStartedAt, setAiPlanStartedAt] = useState<number | null>(null);
  const [aiPlanElapsedSeconds, setAiPlanElapsedSeconds] = useState(0);
  const [aiPlanApplying, setAiPlanApplying] = useState(false);
  const [aiPlanFallbackLoading, setAiPlanFallbackLoading] = useState(false);
  const aiPlanApplyInFlightRef = useRef(false);
  const aiPlanApplyIdempotencyKeyRef = useRef<string | null>(null);
  const aiPlanProtectedBlockIdsRef = useRef<Set<string>>(new Set());
  const aiPlanDroppedBlockIdsRef = useRef<Set<string>>(new Set());
  // Phase 4: AI judge proposes breakdown for too-big steps before
  // we let generatePlanDiff slot them. The dialog owns its draft
  // state; this component just holds the open flag and the
  // proposals to seed it. After confirm we save sub_steps to
  // milestones and re-fire AI Time Box with skipJudge=true.
  const [breakdownProposals, setBreakdownProposals] = useState<StepBreakdownProposal[]>([]);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [pendingAutoPlanAfterBreakdown, setPendingAutoPlanAfterBreakdown] = useState(false);
  const [completingSelectedBlockId, setCompletingSelectedBlockId] = useState<string | null>(null);
  const [scheduleUndoNotice, setScheduleUndoNotice] = useState<ScheduleUndoNotice | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showAllLaterNotes, setShowAllLaterNotes] = useState(false);
  const [showPlanningContextDetails, setShowPlanningContextDetails] = useState(false);
  const [boardInteraction, setBoardInteraction] = useState<BoardInteractionState | null>(null);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [editingTimelineBlockId, setEditingTimelineBlockId] = useState<string | null>(null);
  const [editingTimelineTitle, setEditingTimelineTitle] = useState("");
  const [editingTimelineDescription, setEditingTimelineDescription] = useState("");
  const [editingTimelineStart, setEditingTimelineStart] = useState("");
  const [editingTimelineEnd, setEditingTimelineEnd] = useState("");
  const [editingTimelineSaving, setEditingTimelineSaving] = useState(false);
  const [editingTimelineCommitmentLevel, setEditingTimelineCommitmentLevel] = useState<CommitmentLevel>(DEFAULT_COMMITMENT_LEVEL);
  const [editingTimelineDueDate, setEditingTimelineDueDate] = useState("");
  const [editingTimelineDurationDays, setEditingTimelineDurationDays] = useState(1);
  const [editingTimelineLifeArea, setEditingTimelineLifeArea] = useState<string | null>(DEFAULT_LIFE_AREA);
  const [creatingNewBlock, setCreatingNewBlock] = useState(false);
  // Block id receiving the green "✓ Saved" pulse + toast after a successful
  // edit/create. Cleared after the keyframe finishes.
  const [savePulseBlockId, setSavePulseBlockId] = useState<string | null>(null);
  const savePulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pendingRevealBlockId, setPendingRevealBlockId] = useState<string | null>(null);
  const [carryForwardFocusBlockId, setCarryForwardFocusBlockId] = useState<string | null>(null);
  const [dragNotice, setDragNotice] = useState<string | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const boardInteractionRef = useRef<BoardInteractionState | null>(null);
  const pendingBoardMoveRef = useRef<PendingBoardMoveState | null>(null);
  const dragJustEndedRef = useRef(false);
  const blocksRef = useRef<ScheduleBlock[]>([]);
  const aiPlanAbortControllerRef = useRef<AbortController | null>(null);
  const lastAutoOpenPlanTokenRef = useRef(0);
  const suppressNextScheduleSelfRefreshRef = useRef(false);

  useEffect(() => {
    setShowAllLaterNotes(false);
    setCarryForwardFocusBlockId(null);
  }, [date]);

  useEffect(() => {
    if (!aiPlanLoading || aiPlanStartedAt === null) {
      setAiPlanElapsedSeconds(0);
      return;
    }
    const updateElapsed = () => {
      setAiPlanElapsedSeconds(
        Math.floor((Date.now() - aiPlanStartedAt) / 1000),
      );
    };
    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(intervalId);
  }, [aiPlanLoading, aiPlanStartedAt]);

  useEffect(() => {
    return () => {
      aiPlanAbortControllerRef.current?.abort();
    };
  }, []);

  const aiPlanLongRunning = aiPlanLoading && aiPlanElapsedSeconds >= 60;
  const aiPlanStatusText = aiPlanLoading
    ? aiPlanLongRunning
      ? AI_PLAN_LONG_WAIT_COPY
      : AI_PLAN_EXPECTED_WAIT_COPY
    : null;

  const goalLifeAreaLookup = useMemo(() => buildLifeAreaLookup(goals), [goals]);
  const goalLifeAreaLookupRef = useRef<LifeAreaLookup>(goalLifeAreaLookup);

  useEffect(() => {
    goalLifeAreaLookupRef.current = goalLifeAreaLookup;
  }, [goalLifeAreaLookup]);

  const loadData = useCallback(async (opts?: { skipLoading?: boolean; scheduleOnly?: boolean }) => {
    if (!opts?.skipLoading) setLoading(true);
    if (opts?.scheduleOnly) {
      const schedRes = await getSchedule(date).catch(() => ({ blocks: [], date, total: 0 }));
      setBlocks(
        hydrateScheduleBlocks(schedRes.blocks, goalLifeAreaLookupRef.current).sort(
          (a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime()
        )
      );
    } else {
      const [schedRes, goalRes, planningRes] = await Promise.all([
        getSchedule(date).catch(() => ({ blocks: [], date, total: 0 })),
        listGoals("active").catch(() => ({ goals: [], total: 0 })),
        getPlanningOverview(date).catch(() => null),
      ]);
      const scheduleLookup = buildLifeAreaLookup(goalRes.goals);
      const hydrated = hydrateScheduleBlocks(schedRes.blocks, scheduleLookup).sort(
        (a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime()
      );
      // U27 fallback bridge — fill timeline from planning_overview's
      // Google Calendar events when the backend sync hasn't persisted
      // them yet (dedup by external_event_id).
      setBlocks(
        bridgeCalendarEventsIntoBlocks(hydrated, planningRes?.calendar_events ?? [])
      );
      setGoals(goalRes.goals);
      setPlanningOverview(planningRes);
    }
    if (!opts?.skipLoading) setLoading(false);
  }, [date]);

  const lastScheduleChange = useDataRefresh((state) => state.lastScheduleChange);
  const lastGoalChange = useDataRefresh((state) => state.lastGoalChange);
  const lastTaskChange = useDataRefresh((state) => state.lastTaskChange);
  const notifyScheduleChange = useDataRefresh((state) => state.notifyScheduleChange);
  const notifyTaskChange = useDataRefresh((state) => state.notifyTaskChange);
  const notifyGoalChange = useDataRefresh((state) => state.notifyGoalChange);
  const showCrossSurfaceConfirmation = useCrossSurfaceConfirmationStore((state) => state.showConfirmation);

  const hasLoadedOnce = useRef(false);
  useEffect(() => {
    // Show loading spinner only on the very first mount, not on date navigation
    void loadData({ skipLoading: hasLoadedOnce.current });
    hasLoadedOnce.current = true;
  }, [loadData]);

  useEffect(() => {
    if (lastScheduleChange > 0 || lastGoalChange > 0) {
      if (suppressNextScheduleSelfRefreshRef.current && lastScheduleChange > 0) {
        suppressNextScheduleSelfRefreshRef.current = false;
        return;
      }
      // Background sync — skip loading flash since user is already looking at the screen
      void loadData({ skipLoading: true });
    }
    // ADR-0005 (F3): also refetch on a task-only change so completing a task
    // elsewhere updates the timeline without waiting for a tab remount.
  }, [lastScheduleChange, lastGoalChange, lastTaskChange, loadData]);

  useEffect(() => {
    if (!scheduleUndoNotice) return;
    const timeoutId = window.setTimeout(() => setScheduleUndoNotice(null), 5200);
    return () => window.clearTimeout(timeoutId);
  }, [scheduleUndoNotice]);

  useEffect(() => {
    if (!dragNotice) return;
    const timeoutId = window.setTimeout(() => setDragNotice(null), 3000);
    return () => window.clearTimeout(timeoutId);
  }, [dragNotice]);

  useEffect(() => {
    if (!errorMsg) return;
    const timeoutId = window.setTimeout(() => setErrorMsg(null), 3000);
    return () => window.clearTimeout(timeoutId);
  }, [errorMsg]);

  useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);

  useEffect(() => {
    boardInteractionRef.current = boardInteraction;
  }, [boardInteraction]);

  const now = Date.now();
  const nextBlockIdx = blocks.findIndex((block) => !block.is_completed && parseUTCishDate(block.end_time).getTime() > now);
  const visibleBlocks = showAllBlocks ? blocks : blocks.slice(0, Math.max(nextBlockIdx + 3, 3));

  const totalScheduledMinutes = blocks.reduce(
    (sum, block) => sum + blockDurationMinutes(block.start_time, block.end_time),
    0
  );
  const fixedMinutes = blocks
    .filter((block) => block.kind === "calendar")
    .reduce((sum, block) => sum + blockDurationMinutes(block.start_time, block.end_time), 0);
  const optionalMinutes = blocks
    .filter((block) => block.kind === "custom")
    .reduce((sum, block) => sum + blockDurationMinutes(block.start_time, block.end_time), 0);
  const commitmentMinutes = blocks
    .filter((block) => block.kind !== "calendar" && block.kind !== "custom" && block.kind !== "break" && block.kind !== "buffer")
    .reduce((sum, block) => sum + blockDurationMinutes(block.start_time, block.end_time), 0);
  const optionalBlocks = blocks.filter((block) => block.kind === "custom");
  const committedBlocks = blocks.filter(
    (block) => block.kind !== "calendar" && block.kind !== "custom" && block.kind !== "break" && block.kind !== "buffer"
  );
  const activeBoardLifeAreas = useMemo(() => {
    const areas = new Set<string>();
    let hasUnlinked = false;
    let hasRecovery = false;
    for (const block of blocks) {
      if (block.kind === "break" || block.kind === "buffer") {
        hasRecovery = true;
      }
      const area = blockLifeArea(block);
      if (area) {
        areas.add(area);
      } else if (block.kind !== "calendar") {
        hasUnlinked = true;
      }
    }
    return { areas, hasUnlinked, hasRecovery };
  }, [blocks]);
  const selectedDateLabel = new Date(`${date}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const isToday = date === todayStr();
  // "Back to today" placement follows direction-of-travel: if the user
  // is looking at a past day, the button sits to the LEFT of the strip
  // (matching the swipe-back direction); future days put it to the
  // RIGHT. On today itself, no button is needed since "Today" is the
  // active day-pill.
  const todayDirection: "left" | "right" | "none" = isToday
    ? "none"
    : date < todayStr()
      ? "left"
      : "right";
  const calendarEvents = planningOverview?.calendar_events ?? [];
  const openSlots = planningOverview?.open_slots ?? [];
  const calendarSuggestions = planningOverview?.suggestions ?? [];
  const planningOverviewExecutionPatternHint =
    planningOverview?.execution_pattern_summary?.primary_hint ?? null;
  const planningOverviewGoalDriftSummary = planningOverview?.goal_drift_summary ?? null;
  const planningOverviewLifeAllocation = planningOverview?.life_allocation ?? null;
  const planningOverviewRankedDriftingGoals = useMemo(() => {
    if ((planningOverviewGoalDriftSummary?.top_drifting_goals?.length ?? 0) > 0) {
      return planningOverviewGoalDriftSummary?.top_drifting_goals ?? [];
    }
    if (planningOverviewGoalDriftSummary?.primary_goal) {
      return [planningOverviewGoalDriftSummary.primary_goal];
    }
    return [];
  }, [planningOverviewGoalDriftSummary]);
  const goalFocusTarget = useGoalFocusContext((state) => state.target);
  const setGoalFocusTarget = useGoalFocusContext((state) => state.setTarget);
  const clearGoalFocusTarget = useGoalFocusContext((state) => state.clearTarget);

  // Multi-select set for AI Time Box: which goals should be planned
  // around when the user fires AI Time Box. The single goalFocusTarget
  // (above) is kept for the anchor-highlight UI on the trajectory rail
  // and the focus card; this set is the *plan input*. localStorage
  // persists the selection across reloads — when the user comes back
  // tomorrow they don't have to re-pick the same goals. Reset only
  // happens when the user explicitly clears.
  const [planningFocusGoalIds, setPlanningFocusGoalIds] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem("planning_focus_goal_ids_v1");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setPlanningFocusGoalIds(new Set(parsed.filter((x) => typeof x === "string")));
        }
      }
    } catch {
      /* ignore corrupt storage */
    }
  }, []);

  const persistPlanningFocusGoalIds = useCallback((next: Set<string>) => {
    setPlanningFocusGoalIds(next);
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        "planning_focus_goal_ids_v1",
        JSON.stringify(Array.from(next)),
      );
    } catch {
      /* private mode or quota — ignore */
    }
  }, []);

  const togglePlanningFocusGoal = useCallback(
    (goalId: string) => {
      setPlanningFocusGoalIds((current) => {
        const next = new Set(current);
        if (next.has(goalId)) next.delete(goalId);
        else next.add(goalId);
        if (typeof window !== "undefined") {
          try {
            window.localStorage.setItem(
              "planning_focus_goal_ids_v1",
              JSON.stringify(Array.from(next)),
            );
          } catch {
            /* ignore */
          }
        }
        return next;
      });
    },
    [],
  );

  const clearPlanningFocusGoals = useCallback(() => {
    persistPlanningFocusGoalIds(new Set());
  }, [persistPlanningFocusGoalIds]);

  // Reconcile persisted focus state with the backend-fresh active
  // goal list. Without this, archiving / completing / deleting a
  // goal in GoalsScreen leaves zombie ids in goalFocusTarget and
  // planningFocusGoalIds (both persisted), so the trajectory rail
  // resurrects archived goals and AI Time Box sends invalid ids.
  // We wait until at least one load finished (loading=false) so we
  // don't clear state during the very first render before goals
  // have arrived.
  useEffect(() => {
    if (loading) return;
    const activeIds = new Set(goals.map((g) => g.id));
    if (goalFocusTarget?.goalId && !activeIds.has(goalFocusTarget.goalId)) {
      clearGoalFocusTarget();
    }
    if (planningFocusGoalIds.size > 0) {
      let needsUpdate = false;
      const filtered = new Set<string>();
      planningFocusGoalIds.forEach((id) => {
        if (activeIds.has(id)) filtered.add(id);
        else needsUpdate = true;
      });
      if (needsUpdate) persistPlanningFocusGoalIds(filtered);
    }
  }, [
    loading,
    goals,
    goalFocusTarget,
    planningFocusGoalIds,
    clearGoalFocusTarget,
    persistPlanningFocusGoalIds,
  ]);

  const carryForwardSummary = useCarryForwardContext((state) => state.summary);
  const persistedPlannedFocus = useCarryForwardContext((state) => state.plannedFocus);
  const setCarryForwardSummary = useCarryForwardContext((state) => state.setSummary);
  const setPersistedPlannedFocus = useCarryForwardContext((state) => state.setPlannedFocus);
  const clearPersistedPlannedFocus = useCarryForwardContext((state) => state.clearPlannedFocus);
  const clearCarryForwardSummary = useCarryForwardContext((state) => state.clearSummary);
  const resolveCarryForwardSuggestedFocus = useCarryForwardContext((state) => state.resolveSuggestedFocus);
  const crossSurfaceConfirmation = useCrossSurfaceConfirmationStore((state) => state.confirmation);
  const plannedFocusForCurrentDate =
    persistedPlannedFocus?.date === date ? persistedPlannedFocus : null;
  const displayCarryForwardSummary = plannedFocusForCurrentDate ? null : carryForwardSummary;
  const carryForwardFocusHint = carryForwardSummary ? buildCarryForwardFocusHint(carryForwardSummary) : null;
  const executionPatternHint =
    carryForwardSummary?.execution_pattern_hint ??
    plannedFocusForCurrentDate?.executionPatternHint ??
    planningOverviewExecutionPatternHint ??
    null;
  const executionPatternAction = buildExecutionPatternNextStep(executionPatternHint);
  const buildGoalRecoveryTarget = useCallback(
    (goal: GoalDriftGoal): GoalFocusTarget => ({
      goalId: goal.id,
      goalTitle: sanitizeSurfaceTitle(goal.title),
      headline: `${sanitizeSurfaceTitle(goal.title)} is drifting`,
      message:
        goal.recovery_note ??
        planningOverviewGoalDriftSummary?.message ??
        "This goal has started losing traction. Give it one earlier restart block before protecting more time for anything else.",
      nextStep:
        planningOverviewGoalDriftSummary?.next_step ??
        "Give this goal one lighter restart block and repair the next step before expanding the plan.",
      source: "schedule",
    }),
    [planningOverviewGoalDriftSummary]
  );
  const focusedGoalRecoverySummary = useMemo(() => {
    if (!goalFocusTarget) return null;

    const matchedGoal = goals.find((goal) => goal.id === goalFocusTarget.goalId) ?? null;
    const matchedDriftGoal =
      planningOverviewRankedDriftingGoals.find((goal) => goal.id === goalFocusTarget.goalId) ?? null;
    const totalMilestones = matchedGoal?.milestones.length ?? 0;
    const completedMilestones =
      matchedGoal?.milestones.filter((milestone) => milestone.completed).length ?? 0;
    const orderedDriftingGoals = matchedDriftGoal
      ? [
          matchedDriftGoal,
          ...planningOverviewRankedDriftingGoals.filter((goal) => goal.id !== matchedDriftGoal.id),
        ]
      : planningOverviewRankedDriftingGoals;

    return {
      window_days: planningOverviewGoalDriftSummary?.window_days ?? 14,
      total_goal_count: planningOverviewGoalDriftSummary?.total_goal_count ?? Math.max(goals.length, 1),
      drifting_goal_count: Math.max(1, planningOverviewGoalDriftSummary?.drifting_goal_count ?? 1),
      steady_goal_count:
        planningOverviewGoalDriftSummary?.steady_goal_count ??
        Math.max((goals.length || 1) - 1, 0),
      kind: "drifting_goal" as const,
      headline:
        goalFocusTarget.headline ??
        (matchedDriftGoal
          ? `${sanitizeSurfaceTitle(matchedDriftGoal.title)} is drifting`
          : `${sanitizeSurfaceTitle(goalFocusTarget.goalTitle)} needs a clearer restart`),
      message:
        goalFocusTarget.message ??
        (matchedDriftGoal?.recovery_note ? sanitizeSurfaceTitle(matchedDriftGoal.recovery_note) : null) ??
        "This goal has started losing traction. Give it one earlier restart block before protecting more time for anything else.",
      next_step:
        goalFocusTarget.nextStep ??
        "Give this goal one lighter restart block and repair the next step before expanding the plan.",
      cta_label: "Restart this goal",
      cta_destination: "goals" as const,
      primary_goal: {
        id: goalFocusTarget.goalId,
        title: goalFocusTarget.goalTitle,
        category: matchedDriftGoal?.category ?? matchedGoal?.category ?? null,
        progress_pct: matchedDriftGoal?.progress_pct ?? matchedGoal?.progress_pct ?? 0,
        target_date: matchedDriftGoal?.target_date ?? matchedGoal?.target_date ?? null,
        open_task_count: matchedDriftGoal?.open_task_count ?? 0,
        recent_completed_count: matchedDriftGoal?.recent_completed_count ?? 0,
        recent_rollover_count: matchedDriftGoal?.recent_rollover_count ?? 0,
        total_milestones: matchedDriftGoal?.total_milestones ?? totalMilestones,
        completed_milestones: matchedDriftGoal?.completed_milestones ?? completedMilestones,
        days_since_activity: matchedDriftGoal?.days_since_activity ?? null,
        drift_score: matchedDriftGoal?.drift_score ?? 0,
        recovery_note: matchedDriftGoal?.recovery_note ?? null,
        // Carry the per-goal next_step through the locally-synthesized
        // primary_goal so the ranked-list UI (which may read this
        // shape when no real drift summary is loaded yet) can render
        // its "Best next move" line. Falls back to the focus target's
        // nextStep, then the matchedDriftGoal's own next_step, then
        // null.
        is_drifting: matchedDriftGoal?.is_drifting ?? false,
        next_step:
          matchedDriftGoal?.next_step ??
          goalFocusTarget.nextStep ??
          firstUncompletedSchedulableTitle(matchedGoal?.milestones),
      },
      top_drifting_goals: orderedDriftingGoals,
    };
  }, [goalFocusTarget, goals, planningOverviewGoalDriftSummary, planningOverviewRankedDriftingGoals]);
  const goalDriftSummaryContext = focusedGoalRecoverySummary ?? planningOverviewGoalDriftSummary;
  const goalDriftPrimaryGoal = goalDriftSummaryContext?.primary_goal ?? null;
  const goalDriftRankedGoals = useMemo(() => {
    const base =
      (goalDriftSummaryContext?.top_drifting_goals?.length ?? 0) > 0
        ? goalDriftSummaryContext?.top_drifting_goals ?? []
        : goalDriftPrimaryGoal
          ? [goalDriftPrimaryGoal]
          : [];
    // Planning-focused goals always sort to the top so the user
    // sees what they queued without scanning. Within each tier, we
    // preserve the backend's drift-score ordering.
    if (planningFocusGoalIds.size === 0) return base;
    const focused: typeof base = [];
    const rest: typeof base = [];
    for (const goal of base) {
      if (planningFocusGoalIds.has(goal.id)) focused.push(goal);
      else rest.push(goal);
    }
    return [...focused, ...rest];
  }, [goalDriftPrimaryGoal, goalDriftSummaryContext, planningFocusGoalIds]);
  const goalDriftPrimaryGoalPct = goalDriftPrimaryGoal
    ? Math.round(goalDriftPrimaryGoal.progress_pct * 100)
    : 0;
  const {
    visibleSupportGoals: visiblePlanSupportGoals,
    hiddenGoalCount: hiddenPlanGoalCount,
  } = useMemo(
    () =>
      buildPlanSupportGoals({
        goals,
        goalDriftSummary: goalDriftSummaryContext,
        focusGoalId: goalFocusTarget?.goalId ?? null,
        lifeAllocation: planningOverviewLifeAllocation,
      }),
    [goalDriftSummaryContext, goalFocusTarget?.goalId, goals, planningOverviewLifeAllocation]
  );
  const carryForwardFocusBadgeLabel =
    (plannedFocusForCurrentDate ? "Start here" : null) ||
    getCarryForwardFocusBadgeLabel(displayCarryForwardSummary) ||
    (plannedFocusForCurrentDate ? "Start here" : null);
  const scheduleCarryForwardPanelTitle = displayCarryForwardSummary
    ? "Carry-forward focus"
    : plannedFocusForCurrentDate
      ? "Today's start block"
      : null;
  const scheduleCarryForwardPanelBody = displayCarryForwardSummary
    ? null
    : plannedFocusForCurrentDate
      ? `Your earlier plan already picked ${sanitizeSurfaceTitle(plannedFocusForCurrentDate.title)} as today's restart block.`
      : null;
  const scheduleCarryForwardPanelHint =
    (plannedFocusForCurrentDate ? `Start with ${sanitizeSurfaceTitle(plannedFocusForCurrentDate.title)}.` : null) ||
    carryForwardFocusHint;
  const hasPlanningContext = Boolean(
    displayCarryForwardSummary || plannedFocusForCurrentDate || executionPatternHint || goalDriftSummaryContext
  );
  const hiddenPlanningCueCount =
    (executionPatternHint ? 1 : 0) +
    (goalDriftSummaryContext ? 1 : 0);
  const visibleDayStrip = useMemo(() => buildDayStrip(date), [date]);
  const threeDayStrip = useMemo(() => {
    return Array.from({ length: 3 }, (_, index) => {
      const offset = index - 1;
      const value = shiftDate(date, offset);
      const parsed = new Date(`${value}T12:00:00`);
      return {
        value,
        label: parsed.toLocaleDateString("en-US", { weekday: "short" }),
        day: parsed.getDate(),
      };
    });
  }, [date]);
  const [planRightRailCollapsed, setPlanRightRailCollapsed] = useState<boolean>(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem("de_plan_right_collapsed");
    if (stored === "true") setPlanRightRailCollapsed(true);
  }, []);
  const togglePlanRightRail = useCallback(() => {
    setPlanRightRailCollapsed((current) => {
      const next = !current;
      if (typeof window !== "undefined") {
        window.localStorage.setItem("de_plan_right_collapsed", next ? "true" : "false");
      }
      return next;
    });
  }, []);
  const scheduleBoardMeta = openSlots.length > 0
    ? `${blocks.length} ${blocks.length === 1 ? "block" : "blocks"} planned · ${openSlots.length} open window${openSlots.length === 1 ? "" : "s"} left`
    : `${blocks.length} ${blocks.length === 1 ? "block" : "blocks"} planned`;
  const hasActiveGoals = goals.length > 0;
  const calendarNeedsConnection =
    planningOverview?.calendar_status === "disconnected" || planningOverview?.calendar_status === "error";
  const calendarContextQuiet =
    calendarNeedsConnection &&
    calendarEvents.length === 0 &&
    openSlots.length === 0 &&
    calendarSuggestions.length === 0;
  const selectedBlock = useMemo(
    () => blocks.find((block) => block.id === selectedNoteId) ?? null,
    [blocks, selectedNoteId]
  );
  const selectedBlockIsCarryForwardFocus = Boolean(
    selectedBlock &&
      !selectedBlock.is_completed &&
      (carryForwardFocusBlockId
        ? selectedBlock.id === carryForwardFocusBlockId
        : carryForwardSummary &&
          matchesCarryForwardFocusTitle(displayBlockTitle(selectedBlock), carryForwardSummary))
  );
  const selectedBlockMatchesGoalFocus = Boolean(
    selectedBlock &&
      !selectedBlock.is_completed &&
      matchesGoalFocusTarget(selectedBlock, goalFocusTarget)
  );
  const laterBoardPresentation = useMemo(() => {
    const sortedLaterBlocks = [...optionalBlocks].sort(
      (a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime()
    );
    const activeLaterId = boardInteraction?.blockId ?? null;
    const pinnedIds = new Set<string>();

    if (selectedNoteId && sortedLaterBlocks.some((block) => block.id === selectedNoteId)) {
      pinnedIds.add(selectedNoteId);
    }
    if (activeLaterId && sortedLaterBlocks.some((block) => block.id === activeLaterId)) {
      pinnedIds.add(activeLaterId);
    }

    if (
      showAllLaterNotes ||
      sortedLaterBlocks.length <= MAX_VISIBLE_LATER_NOTES + pinnedIds.size
    ) {
      return {
        visibleLaterBlocks: sortedLaterBlocks,
        hiddenLaterBlocks: [] as ScheduleBlock[],
        summary: null as LaterBoardSummary | null,
      };
    }

    const visibleLaterIds = new Set<string>();
    for (const block of sortedLaterBlocks.slice(0, MAX_VISIBLE_LATER_NOTES)) {
      visibleLaterIds.add(block.id);
    }
    pinnedIds.forEach((id) => {
      visibleLaterIds.add(id);
    });

    const visibleLaterBlocks = sortedLaterBlocks.filter((block) => visibleLaterIds.has(block.id));
    const hiddenLaterBlocks = sortedLaterBlocks.filter((block) => !visibleLaterIds.has(block.id));

    if (hiddenLaterBlocks.length === 0) {
      return {
        visibleLaterBlocks,
        hiddenLaterBlocks,
        summary: null as LaterBoardSummary | null,
      };
    }

    const earliestHidden = hiddenLaterBlocks[0];
    const latestHidden = hiddenLaterBlocks.reduce(
      (latest, block) =>
        parseUTCishDate(block.end_time).getTime() > parseUTCishDate(latest.end_time).getTime()
          ? block
          : latest,
      hiddenLaterBlocks[0]
    );

    return {
      visibleLaterBlocks,
      hiddenLaterBlocks,
      summary: {
        count: hiddenLaterBlocks.length,
        startTime: earliestHidden.start_time,
        endTime: latestHidden.end_time,
      },
    };
  }, [boardInteraction?.blockId, optionalBlocks, selectedNoteId, showAllLaterNotes]);

  const laterBoardSummary = laterBoardPresentation.summary;
  // All blocks the user is currently *inside* (start ≤ now ≤ end).
  // Used by the NOW card so parallel/overlapping blocks can be shown
  // alongside the primary one — single-track scheduling assumes too
  // much. Recomputed on every render via Date.now() in boardRhythm.
  const allActiveNowBlocks = useMemo(() => {
    if (!isToday) return [] as ScheduleBlock[];
    const now = Date.now();
    return blocks
      .filter((block) => {
        const start = parseUTCishDate(block.start_time).getTime();
        const end = parseUTCishDate(block.end_time).getTime();
        return start <= now && end >= now;
      })
      .sort(
        (a, b) =>
          parseUTCishDate(a.start_time).getTime() -
          parseUTCishDate(b.start_time).getTime(),
      );
  }, [blocks, isToday]);

  const boardRhythm = useMemo(() => {
    const now = Date.now();
    const sorted = [...blocks].sort(
      (a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime()
    );
    const activeNow = isToday
      ? sorted.find((block) => {
          const start = parseUTCishDate(block.start_time).getTime();
          const end = parseUTCishDate(block.end_time).getTime();
          return start <= now && end >= now;
        }) ?? null
      : null;
    const nextProtected = sorted.find((block) => {
      if (block.kind === "custom") return false;
      if (!isToday) return true;
      return parseUTCishDate(block.end_time).getTime() > now;
    }) ?? null;
    const nextOpening = openSlots.find((slot) => {
      if (!isToday) return true;
      return parseUTCishDate(slot.end).getTime() > now;
    }) ?? (isToday ? null : openSlots[0] ?? null);
    const laterCount = optionalBlocks.filter((block) => {
      if (!isToday) return true;
      return parseUTCishDate(block.start_time).getTime() >= now;
    }).length;

    return [
      activeNow
        ? {
            label: "Now",
            title: compactSurfaceLabel(displayBlockTitle(activeNow), 34),
            detail: formatTimeRange(activeNow.start_time, activeNow.end_time),
            tone: "border-blue/15 bg-blue/5",
            labelTone: "text-blue",
            block: activeNow as ScheduleBlock | null,
          }
        : nextOpening
          ? {
              label: isToday ? "Now" : "First",
              title: `Calm opening ${formatContextTime(nextOpening.start)}`,
              detail: `${formatContextTime(nextOpening.start)} - ${formatContextTime(nextOpening.end)}`,
              tone: "border-blue/15 bg-blue/5",
              labelTone: "text-blue",
              block: null as ScheduleBlock | null,
            }
          : {
              label: isToday ? "Now" : "First",
              title: "No clear opening yet",
              detail: "Protect one clean block before adding more.",
              tone: "border-blue/15 bg-blue/5",
              labelTone: "text-blue",
              block: null as ScheduleBlock | null,
            },
      nextProtected
        ? {
            label: "Anchor",
            title: compactSurfaceLabel(displayBlockTitle(nextProtected), 34),
            detail: formatTimeRange(nextProtected.start_time, nextProtected.end_time),
            tone: "border-forest/15 bg-sage-pale/45",
            labelTone: "text-forest-med",
            block: null as ScheduleBlock | null,
          }
        : {
            label: "Anchor",
            title: "No anchor block yet",
            detail: "Place one meaningful task before the optional notes.",
            tone: "border-forest/15 bg-sage-pale/45",
            labelTone: "text-forest-med",
            block: null as ScheduleBlock | null,
          },
      laterCount > 0
        ? {
            label: "Later",
            title: `${laterCount} later note${laterCount === 1 ? "" : "s"}`,
            detail:
              laterBoardSummary && !showAllLaterNotes
                ? `${laterBoardSummary.count} tucked into one quieter stack.`
                : "Only if the day stays light.",
            tone: "border-amber/15 bg-amber/5",
            labelTone: "text-amber",
            block: null as ScheduleBlock | null,
          }
        : nextOpening
          ? {
              label: "Later",
              title: "Keep the back half light",
              detail: `Best extra space starts around ${formatContextTime(nextOpening.start)}.`,
              tone: "border-amber/15 bg-amber/5",
              labelTone: "text-amber",
              block: null as ScheduleBlock | null,
            }
          : {
              label: "Later",
              title: "Leave some breathing room",
              detail: "Optional notes belong in the quieter hours.",
              tone: "border-amber/15 bg-amber/5",
              labelTone: "text-amber",
              block: null as ScheduleBlock | null,
            },
    ];
  }, [blocks, isToday, laterBoardSummary, openSlots, optionalBlocks, showAllLaterNotes]);
  const nowIndicatorTop = useMemo(() => {
    if (!isToday) return null;
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes() - timelineStartHour * 60;
    if (minutes < 0 || minutes > totalTimelineMinutes) return null;
    return minutes * PX_PER_MINUTE;
  }, [isToday, timelineStartHour, totalTimelineMinutes]);

  const loadDataAndClearDrag = useCallback(async () => {
    setBoardInteraction(null);
    await loadData();
  }, [loadData]);

  const buildPreviewTimes = useCallback(
    (block: ScheduleBlock, topPx: number, durationMinutes: number) => {
      const minutesFromStart = Math.round(topPx / PX_PER_MINUTE);
      const totalMinutesOfDay = timelineStartHour * 60 + minutesFromStart;
      const nextHour = Math.floor(totalMinutesOfDay / 60);
      const nextMinute = totalMinutesOfDay % 60;
      const nextStart = parseUTCishDate(block.start_time);
      nextStart.setHours(nextHour, nextMinute, 0, 0);
      const nextEnd = new Date(nextStart.getTime() + durationMinutes * 60000);
      return {
        startISO: nextStart.toISOString(),
        endISO: nextEnd.toISOString(),
      };
    },
    // Must depend on `timelineStartHour` — when auto-expand kicks
    // in (a block earlier than user's working window forces the
    // timeline to start at e.g. 8 AM instead of 9 AM), the drag-
    // preview math has to use the NEW baseline. Without this dep,
    // drag callbacks captured the original hour and every drag
    // landed 60 min off (the screenshot you sent: 2:35 PM block
    // rendering at 3:35 PM position).
    [timelineStartHour]
  );

  const boardBlocks = useMemo(() => {
    const visibleLaterIds = new Set(
      laterBoardPresentation.visibleLaterBlocks.map((block) => block.id)
    );
    return blocks
      .filter((block) => block.kind !== "custom" || visibleLaterIds.has(block.id))
      .sort(
        (a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime()
      );
  }, [blocks, laterBoardPresentation.visibleLaterBlocks]);

  const chatRevealPlanKey = useMemo(
    () => blocks.map((block) => block.id).join("|"),
    [blocks],
  );
  useChatRevealHighlight("plan", chatRevealPlanKey);

  // CLCD ship-blocker P0-1 — pulse is handled entirely by the existing
  // `useChatRevealHighlight("plan", chatRevealPlanKey)` above, which
  // re-runs whenever the joined block ids change (so the highlight
  // fires once blocks for the lazy-init date load). The lazy init for
  // `date` (a few lines up) ensures Plan mounts on the target date so
  // the element with the matching `data-reveal-id` is in the DOM by
  // the time the highlight effect re-fires.
  //
  // We intentionally do NOT have a ScheduleGoals-level useEffect that
  // calls `clearActive(active.id)` here. page.tsx subscribes to
  // `chatRevealStore.pending` and bumps `tabVisitCount` when pending
  // is set (page.tsx:127). That bump REMOUNTS ScheduleGoalsScreen via
  // the `key={schedule-${count}-...}` prop. If our effect cleared
  // active before the remount, the second mount's lazy init would see
  // `active=null` and fall back to `todayStr()` — putting the user
  // back on today's view. Verified live by the dogfood at
  // dogfood-output/clcd-combined-shipblocker-2026-05-28 (the
  // June-15-then-May-28 fetch sequence). Let `useChatRevealHighlight`
  // own the `active` lifecycle; it clears after its own 1500ms pulse,
  // which is AFTER both remounts have settled.

  const boardLayouts = useMemo(() => {
    const entries = boardBlocks.map((block) => {
      if (boardInteraction?.blockId === block.id) {
        const { startISO, endISO } = buildPreviewTimes(block, boardInteraction.currentTop, boardInteraction.currentDuration);
        return {
          id: block.id,
          startMs: parseUTCishDate(startISO).getTime(),
          endMs: parseUTCishDate(endISO).getTime(),
        };
      }
      return {
        id: block.id,
        startMs: parseUTCishDate(block.start_time).getTime(),
        endMs: parseUTCishDate(block.end_time).getTime(),
      };
    });
    return computeBoardLayouts(entries);
  }, [boardBlocks, boardInteraction, buildPreviewTimes]);

  const findConflictTitle = useCallback((blockId: string, startISO: string, endISO: string) => {
      const previewStart = parseUTCishDate(startISO).getTime();
      const previewEnd = parseUTCishDate(endISO).getTime();
    const overlap = blocksRef.current.find((block) => {
      if (block.id === blockId) return false;
      const blockStart = parseUTCishDate(block.start_time).getTime();
      const blockEnd = parseUTCishDate(block.end_time).getTime();
      return previewStart < blockEnd && previewEnd > blockStart;
    });
    return overlap ? displayBlockTitle(overlap) : null;
  }, []);

  const interactionPreview = useMemo(() => {
    if (!boardInteraction) return null;
    const block = blocks.find((item) => item.id === boardInteraction.blockId);
    if (!block) return null;
    const { startISO, endISO } = buildPreviewTimes(
      block,
      boardInteraction.currentTop,
      boardInteraction.currentDuration
    );
    return {
      title: displayBlockTitle(block),
      mode: boardInteraction.mode,
      label: formatTimeRange(startISO, endISO),
      lineTop:
        boardInteraction.mode === "resize"
          ? boardInteraction.currentTop + boardInteraction.currentDuration * PX_PER_MINUTE
          : boardInteraction.currentTop,
      conflictTitle: boardInteraction.conflictTitle,
      durationMinutes: boardInteraction.currentDuration,
    };
  }, [boardInteraction, blocks, buildPreviewTimes]);

  const selectedSlotFrame = useMemo(() => {
    if (boardInteraction) {
      return {
        top: boardInteraction.currentTop,
        height: Math.max(28, boardInteraction.currentDuration * PX_PER_MINUTE),
        conflict: Boolean(interactionPreview?.conflictTitle),
        active: true,
      };
    }

    // Suppress the static "selected slot" ghost frame whenever the
    // editorial block-edit modal is open or being created. The modal
    // already paints a terracotta anchor ring directly on the source
    // block, and the dashed frame reads as redundant chrome under it.
    if (editingTimelineBlockId || creatingNewBlock) return null;

    if (!selectedBlock) return null;

    return {
      top: minuteOffset(selectedBlock.start_time, timelineStartHour) * PX_PER_MINUTE,
      height: Math.max(30, blockDurationMinutes(selectedBlock.start_time, selectedBlock.end_time) * PX_PER_MINUTE),
      conflict: false,
      active: false,
    };
  }, [boardInteraction, interactionPreview?.conflictTitle, selectedBlock, editingTimelineBlockId, creatingNewBlock, timelineStartHour]);

  useEffect(() => {
    if (!selectedNoteId) return;
    if (!blocks.some((block) => block.id === selectedNoteId)) {
      setSelectedNoteId(null);
    }
  }, [blocks, selectedNoteId]);

  useEffect(() => {
    if (!selectedNoteId || !timelineRef.current) return;
    const timeline = timelineRef.current;
    const note = timeline.querySelector<HTMLElement>(`[data-note-id="${selectedNoteId}"]`);
    if (!note) return;

    const noteTop = note.offsetTop;
    const noteBottom = noteTop + note.offsetHeight;
    const viewportTop = timeline.scrollTop;
    const viewportBottom = viewportTop + timeline.clientHeight;
    const padding = 32;

    if (noteTop >= viewportTop + padding && noteBottom <= viewportBottom - padding) {
      return;
    }

    const centeredTop = Math.max(0, noteTop - timeline.clientHeight * 0.28);
    timeline.scrollTo({ top: centeredTop, behavior: "smooth" });
  }, [selectedNoteId]);

  // Reveal effects depend on triggerSavePulse — defined further
  // down. They've been moved below it (search for "land + pulse").

  useEffect(() => {
    if (goalFocusTarget) {
      setShowPlanningContextDetails(true);
    }
  }, [goalFocusTarget]);

  useEffect(() => {
    if (!persistedPlannedFocus || persistedPlannedFocus.date !== date) return;
    const matchingBlock = blocks.find((block) => block.id === persistedPlannedFocus.blockId);
    if (!matchingBlock || matchingBlock.is_completed) return;
    setCarryForwardFocusBlockId((current) => current ?? persistedPlannedFocus.blockId);
    setSelectedNoteId((current) => current ?? persistedPlannedFocus.blockId);
  }, [blocks, date, persistedPlannedFocus]);

  useEffect(() => {
    if (!persistedPlannedFocus || persistedPlannedFocus.date !== date || loading) return;
    const matchingBlock = blocks.find((block) => block.id === persistedPlannedFocus.blockId);
    if (!matchingBlock || matchingBlock.is_completed) {
      if (carryForwardFocusBlockId === persistedPlannedFocus.blockId) {
        setCarryForwardFocusBlockId(null);
      }
      if (selectedNoteId === persistedPlannedFocus.blockId) {
        setSelectedNoteId(null);
      }
      clearPersistedPlannedFocus();
    }
  }, [
    blocks,
    carryForwardFocusBlockId,
    clearPersistedPlannedFocus,
    date,
    loading,
    persistedPlannedFocus,
    selectedNoteId,
  ]);

  const handleAddGoal = async () => {
    if (!newGoalTitle.trim()) return;
    setCreating(true);
    setErrorMsg(null);
    try {
      const result = await createGoal({ title: newGoalTitle.trim() });
      setNewGoalTitle("");
      setShowAddGoal(false);
      if (result.id) setRetroGoalId(result.id);
      await loadData();
    } catch {
      setErrorMsg("Failed to create goal. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const [aiPlanCommitmentLevel, setAiPlanCommitmentLevel] = useState<CommitmentLevel>("planned");

  const aiPlanPreferences = useMemo(() => {
    const preferences: Record<string, unknown> = {
      tz_offset: new Date().getTimezoneOffset(),
    };
    const plannedFocusForCurrentDate =
      persistedPlannedFocus?.date === date && persistedPlannedFocus.title ? persistedPlannedFocus : null;
    const displayCarryForwardSummary = plannedFocusForCurrentDate ? null : carryForwardSummary;
    if (displayCarryForwardSummary?.suggested_focus_title) {
      preferences.carry_forward_focus_title = sanitizeSurfaceTitle(displayCarryForwardSummary.suggested_focus_title);
    }
    if (displayCarryForwardSummary?.suggested_focus_kind) {
      preferences.carry_forward_focus_kind = displayCarryForwardSummary.suggested_focus_kind;
    }
    if (executionPatternHint?.kind) {
      preferences.execution_pattern_hint_kind = executionPatternHint.kind;
    }
    if (executionPatternHint?.title) {
      preferences.execution_pattern_hint_title = sanitizeSurfaceTitle(executionPatternHint.title);
    }
    if (executionPatternHint?.message) {
      preferences.execution_pattern_hint_message = sanitizeSurfaceTitle(executionPatternHint.message);
    }
    if (goalDriftSummaryContext?.kind) {
      preferences.goal_drift_kind = goalDriftSummaryContext.kind;
    }
    if (goalDriftSummaryContext?.primary_goal?.id) {
      preferences.goal_drift_goal_id = goalDriftSummaryContext.primary_goal.id;
    }
    if (goalDriftSummaryContext?.primary_goal?.title) {
      preferences.goal_drift_goal_title = sanitizeSurfaceTitle(goalDriftSummaryContext.primary_goal.title);
    }
    if (!preferences.carry_forward_focus_title && plannedFocusForCurrentDate?.title) {
      preferences.carry_forward_focus_title = sanitizeSurfaceTitle(plannedFocusForCurrentDate.title);
      preferences.carry_forward_focus_kind = "today";
    }
    if (planningOverviewLifeAllocation?.headline) {
      preferences.life_allocation_headline = sanitizeSurfaceTitle(planningOverviewLifeAllocation.headline);
    }
    const allocationAreas = planningOverviewLifeAllocation?.areas ?? [];
    if (allocationAreas.length > 0) {
      // Slider state moved to BalanceReviewScreen (Decide → Balance). Each
      // commit there persists via PATCH /api/life-areas → next planning
      // overview reload. So we just trust the persisted backend values.
      preferences.life_allocation_areas = allocationAreas.map((area) => ({
        area: area.area,
        focus_level: area.focus_level,
        focus_score: area.focus_score,
        target_minutes_per_week: area.target_minutes_per_week,
        target_minutes_per_day: area.target_minutes_per_day,
      }));
    }
    if ((planningOverviewLifeAllocation?.goals?.length ?? 0) > 0) {
      preferences.life_allocation_goals = planningOverviewLifeAllocation?.goals.slice(0, 3).map((goal) => ({
        id: goal.id,
        allocation_mode: goal.allocation_mode,
        target_minutes_per_week: goal.target_minutes_per_week,
        importance_weight: goal.importance_weight,
        willingness_weight: goal.willingness_weight,
        importance_label:
          goal.importance_label ?? goalWeightLabel("importance", goal.importance_weight),
        willingness_label:
          goal.willingness_label ?? goalWeightLabel("willingness", goal.willingness_weight),
      }));
    }
    // The user's explicit "Planning this" choice from the Goal
    // trajectory rail. When set, the AI Time Box backend should
    // anchor its plan around this goal's next_step (single milestone
    // title, derived in goal_drift.py) and not just slot tasks
    // generically. This is distinct from goal_drift_goal_id, which
    // is the algorithm's *suggested* recovery target — the user can
    // pick a different goal to focus on.
    if (goalFocusTarget?.goalId) {
      preferences.planning_focus_goal_id = goalFocusTarget.goalId;
    }
    if (goalFocusTarget?.goalTitle) {
      preferences.planning_focus_goal_title = sanitizeSurfaceTitle(goalFocusTarget.goalTitle);
    }
    // Surface the user-picked goal's next_step (first uncompleted
    // milestone) for the AI to slot. We pull it from `goals` directly
    // — that's the canonical source after Phase 1's edit flow saves
    // back into goal.milestones. Phase 4: when the parent milestone
    // has been broken down into sub_steps, we send the first
    // uncompleted sub_step (the actual schedulable unit), not the
    // parent's bigger title.
    const focusGoalId = goalFocusTarget?.goalId ?? null;
    if (focusGoalId) {
      const focusGoal = goals.find((g) => g.id === focusGoalId) ?? null;
      const nextStep = firstUncompletedSchedulableTitle(focusGoal?.milestones);
      if (nextStep) {
        preferences.planning_focus_next_step = sanitizeSurfaceTitle(nextStep);
      }
    }

    // Multi-goal selection: build a parallel array of every goal the
    // user has queued via the trajectory-card toggle. Backend treats
    // matching ANY of these as the highest-priority sort signal, so
    // tasks linked to selected goals all come first. Empty array =
    // no multi-selection, fall back to single-goal preferences above
    // (which themselves fall back to general planning).
    if (planningFocusGoalIds.size > 0) {
      const focusGoals: Array<{
        goal_id: string;
        goal_title?: string;
        next_step?: string;
      }> = [];
      planningFocusGoalIds.forEach((id) => {
        const g = goals.find((item) => item.id === id);
        if (!g) return;
        // Phase 4: schedulable unit is the first uncompleted
        // sub_step when the parent has been broken down, else
        // the parent milestone itself.
        const nextStep = firstUncompletedSchedulableTitle(g.milestones);
        focusGoals.push({
          goal_id: id,
          goal_title: sanitizeSurfaceTitle(g.title),
          ...(nextStep ? { next_step: sanitizeSurfaceTitle(nextStep) } : {}),
        });
      });
      if (focusGoals.length > 0) {
        preferences.planning_focus_goals = focusGoals;
      }
    }
    return preferences;
  }, [
    carryForwardSummary,
    date,
    executionPatternHint,
    goalDriftSummaryContext,
    goalFocusTarget?.goalId,
    goalFocusTarget?.goalTitle,
    goals,
    persistedPlannedFocus,
    planningFocusGoalIds,
    planningOverviewLifeAllocation,
  ]);

  const handleUpdateAiPlanBlock = useCallback(
    (blockId: string, updates: Partial<PlanDraftBlock>) => {
      aiPlanProtectedBlockIdsRef.current.add(blockId);
      setAiPlan((current) => {
        if (!current || current.status !== "ready") return current;

        const nextBlocks = current.blocks.map((block) => {
          if (block.id !== blockId) return block;
          const nextLifeArea =
            updates.life_area === undefined ? block.life_area : updates.life_area;
          return {
            ...block,
            ...updates,
            life_area: nextLifeArea,
            block_metadata:
              updates.life_area === undefined
                ? block.block_metadata
                : {
                    ...(block.block_metadata ?? {}),
                    life_area: nextLifeArea,
                  },
            goal_category:
              updates.goal_category === undefined
                ? (nextLifeArea ?? block.goal_category ?? null)
                : updates.goal_category,
          };
        });

        const syncEntries = (entries: PlanDiffEntry[]) =>
          entries.map((entry) =>
            entry.current.id === blockId
              ? {
                  ...entry,
                  current: {
                    ...entry.current,
                    ...(updates.title !== undefined ? { title: updates.title } : {}),
                    ...(updates.start_time !== undefined ? { start_time: updates.start_time } : {}),
                    ...(updates.end_time !== undefined ? { end_time: updates.end_time } : {}),
                    ...(updates.life_area !== undefined
                      ? { goal_category: updates.life_area }
                      : updates.goal_category !== undefined
                        ? { goal_category: updates.goal_category }
                        : {}),
                    ...(updates.commitment_level !== undefined
                      ? { commitment_level: updates.commitment_level }
                      : {}),
                  },
                }
              : entry
          );

        return deriveAiPlanBoardTruth({
          ...current,
          blocks: nextBlocks,
          adds: syncEntries(current.adds),
          moves: syncEntries(current.moves),
          untouched: syncEntries(current.untouched),
        }, blocksRef.current, goalLifeAreaLookup);
      });
    },
    [goalLifeAreaLookup]
  );

  const openCreateBlockAt = useCallback((minutesFromStart: number) => {
    const snapped = Math.round(minutesFromStart / 15) * 15;
    const startHour = timelineStartHour + Math.floor(snapped / 60);
    const startMin = snapped % 60;
    const endHour = startHour + (startMin + 30 >= 60 ? 1 : 0);
    const endMin = (startMin + 30) % 60;
    setEditingTimelineBlockId("__new__");
    setCreatingNewBlock(true);
    setEditingTimelineTitle("");
    setEditingTimelineDescription("");
    setEditingTimelineStart(`${String(startHour).padStart(2, "0")}:${String(startMin).padStart(2, "0")}`);
    setEditingTimelineEnd(`${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`);
    setEditingTimelineCommitmentLevel(DEFAULT_COMMITMENT_LEVEL);
    setEditingTimelineDueDate("");
    setEditingTimelineDurationDays(1);
    setEditingTimelineLifeArea(DEFAULT_LIFE_AREA);
  }, [timelineStartHour]);

  // Triggers the green pulse + "✓ Saved" toast on a timeline block. The
  // pulse runs for ~900ms; we self-clear so the block returns to its
  // resting style without leaving stuck state.
  const triggerSavePulse = useCallback((blockId: string) => {
    if (savePulseTimerRef.current) clearTimeout(savePulseTimerRef.current);
    setSavePulseBlockId(blockId);
    savePulseTimerRef.current = setTimeout(() => {
      setSavePulseBlockId(null);
    }, 1300);
  }, []);

  useEffect(() => () => {
    if (savePulseTimerRef.current) clearTimeout(savePulseTimerRef.current);
  }, []);

  // Shared "land + pulse" reveal helper. Scrolls the block into view
  // and runs the same golden-frame pulse animation we play on direct
  // edit/create — so cross-surface placements (Today → Plan via
  // capture, AI Time Box apply, etc.) feel landed, not just toast'd.
  const revealAndPulseBlock = useCallback(
    (blockId: string) => {
      setSelectedNoteId(blockId);
      window.setTimeout(() => {
        const el = document.querySelector<HTMLElement>(
          `[data-block-id="${blockId}"]`,
        );
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
      window.setTimeout(() => triggerSavePulse(blockId), 220);
    },
    [triggerSavePulse],
  );

  // Track the last revealNonce we already animated for so neither
  // effect below double-pulses when `blocks` re-resolves after a
  // navigation refresh. Without this guard, every re-render that
  // touches `blocks` re-fires the cross-surface effect against the
  // same active nonce → 2-3 stacked pulses.
  const lastHandledRevealNonceRef = useRef(0);

  useEffect(() => {
    if (!pendingRevealBlockId) return;
    if (!blocks.some((block) => block.id === pendingRevealBlockId)) return;
    if (optionalBlocks.some((block) => block.id === pendingRevealBlockId)) {
      setShowAllLaterNotes(true);
    }
    revealAndPulseBlock(pendingRevealBlockId);
    setPendingRevealBlockId(null);
    if (crossSurfaceConfirmation?.revealNonce) {
      lastHandledRevealNonceRef.current = crossSurfaceConfirmation.revealNonce;
    }
  }, [
    blocks,
    crossSurfaceConfirmation?.revealNonce,
    optionalBlocks,
    pendingRevealBlockId,
    revealAndPulseBlock,
  ]);

  useEffect(() => {
    const revealTarget = crossSurfaceConfirmation?.revealTarget;
    const nonce = crossSurfaceConfirmation?.revealNonce ?? 0;
    if (!revealTarget || nonce <= 0) return;
    if (revealTarget.surface !== "schedule-plan") return;
    // One animation per nonce. Re-runs from `blocks` updates are
    // ignored once we've already handled this confirmation.
    if (nonce === lastHandledRevealNonceRef.current) return;

    if (revealTarget.kind === "schedule_block") {
      if (revealTarget.planDate && revealTarget.planDate !== date) {
        setPendingRevealBlockId(revealTarget.blockId);
        setDate(revealTarget.planDate);
        return;
      }
      // Race guard — capture-commit lands the block on the server,
      // navigates here, then bumps the nonce. The local `blocks`
      // state may not have refreshed yet (loadData is async). If the
      // block isn't in the DOM, scrollIntoView/pulse no-op silently.
      // Defer to the pendingRevealBlockId effect, which fires once
      // the block lands in state.
      if (!blocks.some((block) => block.id === revealTarget.blockId)) {
        setPendingRevealBlockId(revealTarget.blockId);
        return;
      }
      if (optionalBlocks.some((block) => block.id === revealTarget.blockId)) {
        setShowAllLaterNotes(true);
      }
      revealAndPulseBlock(revealTarget.blockId);
      lastHandledRevealNonceRef.current = nonce;
      return;
    }

    if (revealTarget.kind === "goal_focus") {
      window.setTimeout(() => {
        document
          .querySelector<HTMLElement>('[data-testid="schedule-goal-drift-hint"]')
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 120);
      lastHandledRevealNonceRef.current = nonce;
    }
  }, [blocks, crossSurfaceConfirmation, date, optionalBlocks, revealAndPulseBlock]);

  const openTimelineBlockEdit = useCallback((block: ScheduleBlock) => {
    const s = new Date(block.start_time);
    const e = new Date(block.end_time);
    setEditingTimelineBlockId(block.id);
    setEditingTimelineTitle(sanitizeSurfaceTitle(block.title));
    setEditingTimelineDescription(sanitizeSurfaceTitle(block.description ?? ""));
    setEditingTimelineStart(s.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }));
    setEditingTimelineEnd(e.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }));
    setEditingTimelineCommitmentLevel(block.commitment_level ?? DEFAULT_COMMITMENT_LEVEL);
    setEditingTimelineDueDate(block.due_date ? block.due_date.slice(0, 10) : "");
    setEditingTimelineDurationDays(block.duration_days ?? 1);
    setEditingTimelineLifeArea(blockLifeArea(block) ?? DEFAULT_LIFE_AREA);
  }, []);

  const handleAddTimebox = useCallback(() => {
    const preferredOpenSlot = openSlots.find((slot) => slot.duration_minutes >= 30);
    if (preferredOpenSlot) {
      openCreateBlockAt(Math.max(0, Math.min(totalTimelineMinutes - 30, minuteOffset(preferredOpenSlot.start, timelineStartHour))));
      return;
    }

    const nextAnchor = blocks.length > 0
      ? minuteOffset(blocks[blocks.length - 1].end_time, timelineStartHour)
      : Math.max(0, (9 - timelineStartHour) * 60);
    openCreateBlockAt(Math.max(0, Math.min(totalTimelineMinutes - 30, nextAnchor)));
  }, [blocks, openCreateBlockAt, openSlots, timelineStartHour, totalTimelineMinutes]);

  const saveTimelineBlockEdit = useCallback(async () => {
    if (!editingTimelineBlockId) return;

    // Creating a new block
    if (creatingNewBlock) {
      if (!editingTimelineTitle.trim()) return;
      setEditingTimelineSaving(true);
      try {
        const dateObj = new Date(`${date}T12:00:00`);
        const [sh, sm] = editingTimelineStart.split(":").map(Number);
        const [eh, em] = editingTimelineEnd.split(":").map(Number);
        const startISO = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), sh, sm).toISOString();
        const endISO = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), eh, em).toISOString();
        const createdBlocks: ScheduleBlock[] = [];

        for (let d = 0; d < editingTimelineDurationDays; d++) {
          const dayOffset = new Date(dateObj);
          dayOffset.setDate(dayOffset.getDate() + d);
          const dStart = new Date(dayOffset.getFullYear(), dayOffset.getMonth(), dayOffset.getDate(), sh, sm).toISOString();
          const dEnd = new Date(dayOffset.getFullYear(), dayOffset.getMonth(), dayOffset.getDate(), eh, em).toISOString();
          const created = hydrateScheduleBlock(
            await createScheduleBlock({
              title: editingTimelineTitle.trim(),
              description: editingTimelineDescription.trim() || undefined,
              start_time: d === 0 ? startISO : dStart,
              end_time: d === 0 ? endISO : dEnd,
              kind: "task",
              commitment_level: editingTimelineCommitmentLevel,
              life_area: editingTimelineLifeArea ?? undefined,
              due_date: editingTimelineDueDate || undefined,
              duration_days: editingTimelineDurationDays,
            }),
            goalLifeAreaLookup
          );
          createdBlocks.push(created);
        }

        setBlocks((prev) => {
          const nextBlocks = [...prev, ...createdBlocks].sort(
            (a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime()
          );
          blocksRef.current = nextBlocks;
          return nextBlocks;
        });
        suppressNextScheduleSelfRefreshRef.current = true;
        notifyScheduleChange();

        setEditingTimelineBlockId(null);
        setCreatingNewBlock(false);
      } catch {
        /* keep editor open */
      } finally {
        setEditingTimelineSaving(false);
      }
      return;
    }

    const block = blocks.find((b) => b.id === editingTimelineBlockId);
    if (!block) return;
    setEditingTimelineSaving(true);
    try {
      const orig = new Date(block.start_time);
      const [sh, sm] = editingTimelineStart.split(":").map(Number);
      const [eh, em] = editingTimelineEnd.split(":").map(Number);
      const startISO = new Date(orig.getFullYear(), orig.getMonth(), orig.getDate(), sh, sm).toISOString();
      const endISO = new Date(orig.getFullYear(), orig.getMonth(), orig.getDate(), eh, em).toISOString();

      const updatePayload: Record<string, unknown> = {
        title: editingTimelineTitle,
        description: editingTimelineDescription.trim() || null,
        start_time: startISO,
        end_time: endISO,
        commitment_level: editingTimelineCommitmentLevel,
        life_area: editingTimelineLifeArea,
        due_date: editingTimelineDueDate || null,
        duration_days: editingTimelineDurationDays,
      };

      const updated = hydrateScheduleBlock(await updateScheduleBlock(editingTimelineBlockId, updatePayload), goalLifeAreaLookup);
      const createdBlocks: ScheduleBlock[] = [];

      // Multi-day: create blocks on consecutive days if duration_days > 1
      if (editingTimelineDurationDays > 1) {
        const { createScheduleBlock } = await import("@/lib/api");
        for (let d = 1; d < editingTimelineDurationDays; d++) {
          const nextDate = new Date(orig);
          nextDate.setDate(nextDate.getDate() + d);
          const nextStart = new Date(nextDate.getFullYear(), nextDate.getMonth(), nextDate.getDate(), sh, sm).toISOString();
          const nextEnd = new Date(nextDate.getFullYear(), nextDate.getMonth(), nextDate.getDate(), eh, em).toISOString();
          const created = hydrateScheduleBlock(
            await createScheduleBlock({
              title: editingTimelineTitle,
              description: editingTimelineDescription.trim() || undefined,
              start_time: nextStart,
              end_time: nextEnd,
              kind: block.kind,
              commitment_level: editingTimelineCommitmentLevel,
              goal_id: block.goal_id ?? undefined,
              goal_title: block.goal_title ?? undefined,
              life_area: editingTimelineLifeArea ?? undefined,
              due_date: editingTimelineDueDate || undefined,
              duration_days: editingTimelineDurationDays,
            }),
            goalLifeAreaLookup
          );
          createdBlocks.push(created);
        }
      }

      setBlocks((prev) => {
        const nextBlocks = prev
          .map((b) => (b.id === updated.id ? updated : b))
          .concat(createdBlocks)
          .sort((a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime());
        blocksRef.current = nextBlocks;
        return nextBlocks;
      });
      suppressNextScheduleSelfRefreshRef.current = true;
      notifyScheduleChange();

      setEditingTimelineBlockId(null);
    } catch {
      /* keep editor open on failure */
    } finally {
      setEditingTimelineSaving(false);
    }
  }, [
    blocks,
    editingTimelineBlockId,
    editingTimelineCommitmentLevel,
    editingTimelineDescription,
    editingTimelineDueDate,
    editingTimelineDurationDays,
    editingTimelineEnd,
    editingTimelineLifeArea,
    editingTimelineStart,
    editingTimelineTitle,
    goalLifeAreaLookup,
    notifyScheduleChange,
  ]);

  // Build the list of (goal, next_step) pairs the AI judge should
  // size-check before generatePlanDiff slots them. Pulls from the
  // multi-select set first; falls back to the single goalFocusTarget
  // for backward compatibility. Goals with no uncompleted milestone
  // are skipped — there's nothing to judge.
  const collectGoalsToJudge = useCallback((): Array<{
    goalId: string;
    goalTitle: string;
    nextStep: string;
  }> => {
    const out: Array<{ goalId: string; goalTitle: string; nextStep: string }> = [];
    const seen = new Set<string>();
    const pushFromGoal = (g: GoalItem | undefined | null) => {
      if (!g || seen.has(g.id)) return;
      seen.add(g.id);
      // Find the first uncompleted PARENT milestone (not sub_step).
      // If it already has sub_steps, the user accepted a breakdown
      // earlier — those are already-small schedulable units, don't
      // re-judge. The schedulable sub_step still flows to the
      // backend via aiPlanPreferences.planning_focus_goals.
      const parent = g.milestones?.find((m) => !m.completed) ?? null;
      if (!parent) return;
      if (parent.sub_steps && parent.sub_steps.length > 0) return;
      const title = parent.title?.trim();
      if (!title) return;
      out.push({
        goalId: g.id,
        goalTitle: sanitizeSurfaceTitle(g.title),
        nextStep: sanitizeSurfaceTitle(title),
      });
    };
    if (planningFocusGoalIds.size > 0) {
      planningFocusGoalIds.forEach((id) => {
        pushFromGoal(goals.find((g) => g.id === id));
      });
    } else if (goalFocusTarget?.goalId) {
      pushFromGoal(goals.find((g) => g.id === goalFocusTarget.goalId));
    }
    return out;
  }, [goalFocusTarget?.goalId, goals, planningFocusGoalIds]);

  const handleAIPlan = useCallback(
    async (opts?: { skipJudge?: boolean }) => {
      const abortController = new AbortController();
      aiPlanAbortControllerRef.current?.abort();
      aiPlanAbortControllerRef.current = abortController;
      setAiPlanStartedAt(Date.now());
      setAiPlanElapsedSeconds(0);
      setAiPlanLoading(true);
      setErrorMsg(null);
      try {
        // Phase 4: judge step size before we let the planner slot it.
        // Only runs on the user-initiated path; the post-breakdown
        // re-fire passes skipJudge to avoid re-prompting.
        if (!opts?.skipJudge) {
          const candidates = collectGoalsToJudge();
          if (candidates.length > 0) {
            const judged = await Promise.all(
              candidates.map(async (c) => {
                try {
                  const result = await judgeGoalStep(c.goalId, {
                    step_title: c.nextStep,
                    goal_title: c.goalTitle,
                  });
                  return { ...c, result };
                } catch {
                  return null;
                }
              }),
            );
            if (abortController.signal.aborted) return;
            const proposals: StepBreakdownProposal[] = [];
            for (const entry of judged) {
              if (!entry) continue;
              const { result } = entry;
              if (result.verdict !== "too_big") continue;
              const subSteps = (result.sub_steps ?? [])
                .map((s) => s.trim())
                .filter((s) => s.length > 0);
              if (subSteps.length < 2) continue;
              proposals.push({
                goalId: entry.goalId,
                goalTitle: entry.goalTitle,
                parentStep: entry.nextStep,
                reason: result.reason,
                subSteps,
              });
            }
            if (proposals.length > 0) {
              setBreakdownProposals(proposals);
              setBreakdownOpen(true);
              return;
            }
          }
        }

        // FR-1 / FR-18: forward the user's persisted life-area commitments so
        // the closed loop's deterministic cap fires for the AI Time Box
        // preview. 0 minutes is preserved as a hard 'do not schedule' signal.
        let lifeAreaCommits: Record<string, number> | undefined;
        try {
          const { getLifeAreas, deriveLifeAreaCommits } = await import(
            "@/lib/api/balance"
          );
          const { areas: persistedAreas } = await getLifeAreas();
          if (abortController.signal.aborted) return;
          const derived = deriveLifeAreaCommits(persistedAreas);
          if (Object.keys(derived).length > 0) {
            lifeAreaCommits = derived;
          }
        } catch {
          // Best-effort: a failure to fetch life areas falls back to
          // unconstrained scheduling (the env flag also gates the backend).
        }

        const params = {
          date,
          commitment_level: aiPlanCommitmentLevel,
          preferences: aiPlanPreferences,
          life_area_commits: lifeAreaCommits,
        };
        const response = deriveAiPlanBoardTruth(
          await generatePlanDiff(params, { signal: abortController.signal }),
          blocksRef.current,
          goalLifeAreaLookup,
        );
        if (abortController.signal.aborted) return;
        if (response.status === "ready") {
          aiPlanApplyIdempotencyKeyRef.current = makePlanApplyIdempotencyKey(
            response.draft_id,
            response.date,
          );
          aiPlanProtectedBlockIdsRef.current = new Set();
          aiPlanDroppedBlockIdsRef.current = new Set();
        } else {
          aiPlanApplyIdempotencyKeyRef.current = null;
          aiPlanProtectedBlockIdsRef.current = new Set();
          aiPlanDroppedBlockIdsRef.current = new Set();
        }
        setAiPlan(response);
        setCarryForwardFocusBlockId(
          response.status === "ready" ? response.focus_block_id ?? null : null,
        );
      } catch (error) {
        if (
          error instanceof DOMException && error.name === "AbortError" ||
          error instanceof Error && error.name === "AbortError"
        ) {
          setDragNotice("AI Time Box cancelled.");
          return;
        }
        setErrorMsg("Failed to generate AI plan. Please try again.");
      } finally {
        if (aiPlanAbortControllerRef.current === abortController) {
          aiPlanAbortControllerRef.current = null;
        }
        setAiPlanLoading(false);
        setAiPlanStartedAt(null);
      }
    },
    [
      aiPlanCommitmentLevel,
      aiPlanPreferences,
      collectGoalsToJudge,
      date,
      goalLifeAreaLookup,
    ],
  );

  const handleCancelAIPlan = useCallback(() => {
    if (!aiPlanLoading && !aiPlanAbortControllerRef.current) return;
    aiPlanAbortControllerRef.current?.abort();
    aiPlanAbortControllerRef.current = null;
    setAiPlanLoading(false);
    setAiPlanStartedAt(null);
    setAiPlanElapsedSeconds(0);
    setDragNotice("AI Time Box cancelled.");
  }, [aiPlanLoading]);

  // After the user confirms a breakdown and we save sub_steps to
  // milestones, we need React to re-render with the new goal state
  // so aiPlanPreferences picks up the smaller steps. This effect
  // fires on the render *after* setGoals lands, then re-triggers
  // handleAIPlan with skipJudge=true so we don't loop.
  useEffect(() => {
    if (!pendingAutoPlanAfterBreakdown) return;
    setPendingAutoPlanAfterBreakdown(false);
    void handleAIPlan({ skipJudge: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoPlanAfterBreakdown]);

  const handleCloseBreakdown = useCallback(() => {
    // Cancel = "thanks but plan around the original (still-large)
    // steps". The user already committed to planning; the dialog was
    // a suggestion they declined. Aborting entirely would leave them
    // with no plan, which feels broken. To truly abort they can
    // deselect the focus goals from the rail.
    setBreakdownOpen(false);
    setBreakdownProposals([]);
    setPendingAutoPlanAfterBreakdown(true);
  }, []);

  const handleConfirmBreakdown = useCallback(
    async (
      accepted: Array<{ goalId: string; parentStep: string; subSteps: string[] }>,
    ) => {
      if (accepted.length === 0) {
        // User toggled every proposal off — close and just plan with
        // the original (still-too-big) steps. Honors user intent.
        handleCloseBreakdown();
        setPendingAutoPlanAfterBreakdown(true);
        return;
      }
      try {
        const updates = await Promise.all(
          accepted.map(async (entry) => {
            const goal = goals.find((g) => g.id === entry.goalId);
            if (!goal) return null;
            const newSubSteps: MilestoneSubStepInput[] = entry.subSteps.map((title) => ({
              title,
              completed: false,
            }));
            // Find the parent milestone matching parentStep title.
            // Match on trimmed/lowered title to absorb whitespace
            // drift between the LLM's echo and the stored value.
            const parentTitle = entry.parentStep.trim().toLowerCase();
            const milestones: MilestoneInput[] = (goal.milestones ?? []).map((m) => {
              const isParent = m.title.trim().toLowerCase() === parentTitle;
              if (!isParent) {
                return {
                  title: m.title,
                  completed: m.completed,
                  completed_at: m.completed_at ?? null,
                  ...(m.sub_steps && m.sub_steps.length > 0
                    ? {
                        sub_steps: m.sub_steps.map((s) => ({
                          title: s.title,
                          completed: s.completed,
                          completed_at: s.completed_at ?? null,
                        })),
                      }
                    : {}),
                };
              }
              return {
                title: m.title,
                completed: m.completed,
                completed_at: m.completed_at ?? null,
                sub_steps: newSubSteps,
              };
            });
            const updated = await replaceGoalMilestones(goal.id, milestones);
            return updated;
          }),
        );
        const updatedById = new Map<string, GoalItem>();
        for (const g of updates) {
          if (g) updatedById.set(g.id, g);
        }
        setGoals((prev) => prev.map((g) => updatedById.get(g.id) ?? g));
        handleCloseBreakdown();
        setPendingAutoPlanAfterBreakdown(true);
      } catch {
        setErrorMsg("Could not save the step breakdown. Please try again.");
      }
    },
    [goals, handleCloseBreakdown],
  );

  useEffect(() => {
    setAiPlan((current) => {
      if (!current || current.status !== "ready") return current;
      return deriveAiPlanBoardTruth(current, blocks, goalLifeAreaLookup);
    });
  }, [blocks, goalLifeAreaLookup]);

  const handleApplyPlan = async () => {
    if (!aiPlan || aiPlan.status !== "ready") return;
    if (aiPlanApplyInFlightRef.current) return;
    aiPlanApplyInFlightRef.current = true;
    setAiPlanApplying(true);
    setErrorMsg(null);
    try {
      if (!aiPlanApplyIdempotencyKeyRef.current) {
        aiPlanApplyIdempotencyKeyRef.current = makePlanApplyIdempotencyKey(
          aiPlan.draft_id,
          aiPlan.date,
        );
      }
      const applied = await applyPlanDiff({
        date: aiPlan.date,
        commitment_level: aiPlanCommitmentLevel,
        blocks: aiPlan.blocks,
        summary: aiPlan.summary,
        focus_block_id: aiPlan.focus_block_id ?? null,
        total_focus_minutes: aiPlan.total_focus_minutes,
        total_break_minutes: aiPlan.total_break_minutes,
        life_area_breakdown: aiPlan.life_area_breakdown,
        draft_id: aiPlan.draft_id ?? null,
        protected_block_ids: Array.from(aiPlanProtectedBlockIdsRef.current),
        dropped_block_ids: Array.from(aiPlanDroppedBlockIdsRef.current),
        idempotency_key: aiPlanApplyIdempotencyKeyRef.current,
      });
      const sortedAppliedBlocks = applied.blocks
        .map((block) => planDraftBlockToScheduleBlock(block, goalLifeAreaLookup))
        .sort((a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime());
      setBlocks(sortedAppliedBlocks);
      setCarryForwardFocusBlockId(applied.focus_block_id ?? null);
      const carryForwardMatchId =
        applied.focus_block_id ??
        (carryForwardSummary
          ? sortedAppliedBlocks.find((block) =>
              matchesCarryForwardFocusTitle(displaySavedResourceBlockTitle(block), carryForwardSummary)
            )?.id ?? null
          : null);
      if (carryForwardMatchId) {
        setSelectedNoteId(carryForwardMatchId);
        const matchedBlock = sortedAppliedBlocks.find((block) => block.id === carryForwardMatchId);
        if (matchedBlock) {
          setPersistedPlannedFocus({
            blockId: matchedBlock.id,
            title: displaySavedResourceBlockTitle(matchedBlock),
            date,
            taskId: matchedBlock.task_id ?? null,
            executionPatternHint: executionPatternHint ?? null,
          });
        }
      } else {
        clearPersistedPlannedFocus();
      }
      notifyTaskChange();
      notifyScheduleChange();
      showCrossSurfaceConfirmation({
        message: "Applied AI plan. Updated the board for today.",
        ctaLabel: "Reveal item",
        ctaTab: "schedule",
        revealTarget: applied.focus_block_id
          ? {
              surface: "schedule-plan",
              kind: "schedule_block",
              blockId: applied.focus_block_id,
            }
          : null,
        truth: buildPlanConfirmationTruth(applied.focus_block_id, "plan_diff"),
      });
      setAiPlan(null);
      // Apply = "I'm done planning around these goals." Clear the
      // multi-select so the next AI Time Box doesn't silently re-use
      // the same set; the user can re-pick fresh from the rail.
      if (planningFocusGoalIds.size > 0) {
        clearPlanningFocusGoals();
      }
      if (goalFocusTarget) {
        clearGoalFocusTarget();
      }
      aiPlanApplyIdempotencyKeyRef.current = null;
      aiPlanProtectedBlockIdsRef.current = new Set();
      aiPlanDroppedBlockIdsRef.current = new Set();
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      console.error("[plan/apply] failed:", err);
      setErrorMsg(`Failed to apply plan: ${detail}`);
    } finally {
      aiPlanApplyInFlightRef.current = false;
      setAiPlanApplying(false);
    }
  };

  const handleEditPlanManually = useCallback(() => {
    if (!aiPlan) return;
    if (aiPlan.status === "ready") {
      setDragNotice("Click any AI draft block in the sheet to edit it before applying.");
      return;
    }

    setAiPlanFallbackLoading(true);
    setErrorMsg(null);
    void createScheduleBlock({
      kind: aiPlan.manual_fallback.kind,
      title: aiPlan.manual_fallback.title,
      description: aiPlan.manual_fallback.description ?? undefined,
      commitment_level: aiPlan.manual_fallback.commitment_level,
      goal_id: aiPlan.manual_fallback.goal_id ?? undefined,
      goal_title: aiPlan.manual_fallback.goal_title ?? undefined,
      life_area: aiPlan.manual_fallback.life_area ?? undefined,
      rationale: aiPlan.preserved_context.note ?? undefined,
      color: aiPlan.manual_fallback.color ?? undefined,
      start_time: aiPlan.manual_fallback.start_time,
      end_time: aiPlan.manual_fallback.end_time,
    })
      .then((createdBlock) => {
        const hydratedBlock = hydrateScheduleBlock(createdBlock, goalLifeAreaLookup);
        setBlocks((prev) =>
          [...prev, hydratedBlock].sort(
            (a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime()
          )
        );
        setShowAllBlocks(true);
        setDragNotice(
          `Added "${displaySavedResourceBlockTitle(hydratedBlock)}" as a manual draft. Move or resize it to place it.`
        );
        notifyScheduleChange();
        showCrossSurfaceConfirmation({
          message: `Added ${displaySavedResourceBlockTitle(hydratedBlock)} to Plan as a manual fallback.`,
          ctaLabel: "Reveal item",
          ctaTab: "schedule-plan",
          revealTarget: {
            surface: "schedule-plan",
            kind: "schedule_block",
            blockId: hydratedBlock.id,
          },
          truth: buildPlanConfirmationTruth(hydratedBlock.id, "schedule"),
        });
        setAiPlan(null);
      })
      .catch(() => {
        setErrorMsg("Could not create the manual fallback block yet.");
      })
      .finally(() => {
        setAiPlanFallbackLoading(false);
      });
  }, [aiPlan, goalLifeAreaLookup, notifyScheduleChange, showCrossSurfaceConfirmation]);

  const handleRetryPlan = useCallback(() => {
    void handleAIPlan();
  }, [handleAIPlan]);

  const handleDismissAiPlan = useCallback(() => {
    aiPlanApplyIdempotencyKeyRef.current = null;
    aiPlanProtectedBlockIdsRef.current = new Set();
    aiPlanDroppedBlockIdsRef.current = new Set();
    setAiPlan(null);
  }, []);

  const handleSavePlanAsLater = useCallback(() => {
    if (!aiPlan || aiPlan.status !== "failed") return;

    setAiPlanFallbackLoading(true);
    setErrorMsg(null);
    void createScheduleBlock({
      kind: aiPlan.save_as_later.kind,
      title: aiPlan.save_as_later.title,
      description: aiPlan.save_as_later.description ?? undefined,
      commitment_level: aiPlan.save_as_later.commitment_level,
      goal_id: aiPlan.save_as_later.goal_id ?? undefined,
      goal_title: aiPlan.save_as_later.goal_title ?? undefined,
      life_area: aiPlan.save_as_later.life_area ?? undefined,
      rationale: aiPlan.preserved_context.note ?? undefined,
      color: aiPlan.save_as_later.color ?? undefined,
      start_time: aiPlan.save_as_later.start_time,
      end_time: aiPlan.save_as_later.end_time,
    })
      .then((createdBlock) => {
        const hydratedBlock = hydrateScheduleBlock(createdBlock, goalLifeAreaLookup);
        setBlocks((prev) =>
          [...prev, hydratedBlock].sort(
            (a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime()
          )
        );
        setShowAllLaterNotes(true);
        setSelectedNoteId(hydratedBlock.id);
        notifyScheduleChange();
        showCrossSurfaceConfirmation({
          message: `Saved ${displaySavedResourceBlockTitle(hydratedBlock)} as a Later note in Plan.`,
          ctaLabel: "Reveal item",
          ctaTab: "schedule-plan",
          revealTarget: {
            surface: "schedule-plan",
            kind: "schedule_block",
            blockId: hydratedBlock.id,
          },
          truth: buildPlanConfirmationTruth(hydratedBlock.id, "schedule"),
        });
        setAiPlan(null);
      })
      .catch(() => {
        setErrorMsg("Could not save that fallback as a Later note yet.");
      })
      .finally(() => {
        setAiPlanFallbackLoading(false);
      });
  }, [aiPlan, goalLifeAreaLookup, notifyScheduleChange, showCrossSurfaceConfirmation]);

  const handleApplyStarterBlocks = useCallback(
    async (templateBlocks: { title: string; description: string; kind: string; start_time: string; end_time: string; commitment_level: string; life_area?: string | null; color?: string | null }[]) => {
      if (!aiPlan || aiPlan.status !== "failed" || templateBlocks.length === 0) return;
      setAiPlanFallbackLoading(true);
      setErrorMsg(null);
      try {
        const created = [] as ScheduleBlock[];
        for (const tb of templateBlocks) {
          const block = await createScheduleBlock({
            kind: tb.kind,
            title: tb.title,
            description: tb.description ?? undefined,
            commitment_level: tb.commitment_level as CommitmentLevel,
            life_area: tb.life_area ?? undefined,
            color: tb.color ?? undefined,
            start_time: tb.start_time,
            end_time: tb.end_time,
          });
          created.push(block);
        }
        const hydrated = created.map((block) => hydrateScheduleBlock(block, goalLifeAreaLookup));
        setBlocks((prev) =>
          [...prev, ...hydrated].sort(
            (a, b) =>
              parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime()
          )
        );
        notifyScheduleChange();
        showCrossSurfaceConfirmation({
          message: `Applied ${created.length} starter block${created.length === 1 ? "" : "s"} to your day.`,
          ctaLabel: "View timeline",
          ctaTab: "schedule-plan",
          revealTarget: { surface: "schedule-plan", kind: "schedule_block", blockId: hydrated[0].id },
          truth: buildPlanConfirmationTruth(hydrated[0].id, "schedule"),
        });
        setAiPlan(null);
      } catch {
        setErrorMsg("Could not apply some starter blocks.");
      } finally {
        setAiPlanFallbackLoading(false);
      }
    },
    [aiPlan, goalLifeAreaLookup, notifyScheduleChange, showCrossSurfaceConfirmation]
  );

  const handleCompleteSelectedBlock = useCallback(async () => {
    if (!selectedBlock || selectedBlock.kind !== "task" || selectedBlock.is_completed) return;

    setCompletingSelectedBlockId(selectedBlock.id);
    setErrorMsg(null);

    try {
      const updated = await completeScheduleBlock(selectedBlock.id);
      const updatedBlocks = blocks
        .map((block) => (block.id === updated.id ? updated : block))
        .sort((a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime());
      const wasCarryForwardFocus =
        selectedBlockIsCarryForwardFocus || carryForwardFocusBlockId === selectedBlock.id;
      const wasGoalFocusRestart = matchesGoalFocusTarget(selectedBlock, goalFocusTarget);
      const shouldRestoreCarryForwardSummary = Boolean(
        carryForwardSummary &&
          matchesCarryForwardFocusTitle(displayBlockTitle(selectedBlock), carryForwardSummary)
      );
      const undoSummarySnapshot = shouldRestoreCarryForwardSummary ? carryForwardSummary : null;
      const undoPlannedFocusSnapshot =
        wasCarryForwardFocus && plannedFocusForCurrentDate ? plannedFocusForCurrentDate : null;
      const undoGoalFocusSnapshot = wasGoalFocusRestart ? goalFocusTarget : null;
      const nextActiveBlock =
        updatedBlocks.find(
          (block) =>
            block.id !== selectedBlock.id &&
            !block.is_completed &&
            parseUTCishDate(block.end_time).getTime() > Date.now()
        ) ??
        updatedBlocks.find((block) => block.id !== selectedBlock.id && !block.is_completed) ??
        null;

      setBlocks(updatedBlocks);
      setSelectedNoteId(nextActiveBlock?.id ?? null);

      if (wasCarryForwardFocus) {
        setCarryForwardFocusBlockId(null);
        clearPersistedPlannedFocus();
      }

      if (wasGoalFocusRestart) {
        clearGoalFocusTarget();
      }

      if (carryForwardSummary && matchesCarryForwardFocusTitle(displayBlockTitle(selectedBlock), carryForwardSummary)) {
        resolveCarryForwardSuggestedFocus();
      }

      if (updated.task_id) {
        setScheduleUndoNotice({
          taskId: updated.task_id,
          blockId: updated.id,
          title: displayBlockTitle(selectedBlock),
          carryForwardSummarySnapshot: undoSummarySnapshot,
          plannedFocusSnapshot: undoPlannedFocusSnapshot,
          goalFocusSnapshot: undoGoalFocusSnapshot,
        });
      } else {
        setScheduleUndoNotice(null);
      }

      notifyTaskChange();
      notifyScheduleChange();
      // ADR-0005: a completed block moves its goal's progress too.
      notifyGoalChange();
      setDragNotice(
        nextActiveBlock
          ? `Finished "${displayBlockTitle(selectedBlock)}". Next up: ${displayBlockTitle(nextActiveBlock)}.`
          : `Finished "${displayBlockTitle(selectedBlock)}".`
      );
    } catch {
      setErrorMsg("Could not mark that block done yet.");
    } finally {
      setCompletingSelectedBlockId(null);
    }
  }, [
    blocks,
    carryForwardFocusBlockId,
    carryForwardSummary,
    clearGoalFocusTarget,
    clearPersistedPlannedFocus,
    goalFocusTarget,
    notifyScheduleChange,
    notifyTaskChange,
    notifyGoalChange,
    plannedFocusForCurrentDate,
    resolveCarryForwardSuggestedFocus,
    selectedBlock,
    selectedBlockIsCarryForwardFocus,
  ]);

  const handleUndoSelectedBlockCompletion = useCallback(async () => {
    if (!scheduleUndoNotice) return;

    const {
      taskId,
      blockId,
      title,
      carryForwardSummarySnapshot,
      plannedFocusSnapshot,
      goalFocusSnapshot,
    } = scheduleUndoNotice;

    setScheduleUndoNotice(null);
    setErrorMsg(null);

    try {
      await undoTaskCompletion(taskId);
      if (carryForwardSummarySnapshot) {
        setCarryForwardSummary(carryForwardSummarySnapshot);
      }
      if (plannedFocusSnapshot) {
        setPersistedPlannedFocus(plannedFocusSnapshot);
        setCarryForwardFocusBlockId(plannedFocusSnapshot.blockId);
        setSelectedNoteId(plannedFocusSnapshot.blockId);
      } else {
        setCarryForwardFocusBlockId(blockId);
        setSelectedNoteId(blockId);
      }
      if (goalFocusSnapshot) {
        setGoalFocusTarget(goalFocusSnapshot);
      }
      notifyTaskChange();
      notifyScheduleChange();
      await loadData();
      setDragNotice(`Restored "${title}".`);
    } catch {
      setErrorMsg("Could not undo that completion.");
      await loadData();
    }
  }, [
    loadData,
    notifyScheduleChange,
    notifyTaskChange,
    scheduleUndoNotice,
    setCarryForwardSummary,
    setGoalFocusTarget,
    setPersistedPlannedFocus,
  ]);

  useEffect(() => {
    if (autoOpenPlanToken <= 0) return;
    if (lastAutoOpenPlanTokenRef.current === autoOpenPlanToken) return;
    if (loading || aiPlanLoading || aiPlanApplying || aiPlan) return;
    lastAutoOpenPlanTokenRef.current = autoOpenPlanToken;
    void handleAIPlan();
  }, [autoOpenPlanToken, loading, aiPlanLoading, aiPlanApplying, aiPlan, handleAIPlan]);

  const beginBoardInteraction = useCallback(
    (event: React.PointerEvent<HTMLElement>, blockId: string, mode: BoardInteractionMode) => {
      if (!timelineRef.current) return;
      const block = blocks.find((item) => item.id === blockId);
      if (!block || block.kind === "calendar") return;

      const target = event.target as HTMLElement;
      if (mode === "move" && target.closest('[data-resize-handle="true"]')) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setSelectedNoteId(blockId);

      const rect = timelineRef.current.getBoundingClientRect();
      const blockTop = minuteOffset(block.start_time, timelineStartHour) * PX_PER_MINUTE;
      const pointerYInTimeline = event.clientY - rect.top + timelineRef.current.scrollTop;
      const durationMinutes = blockDurationMinutes(block.start_time, block.end_time);

      setBoardInteraction({
        blockId,
        mode,
        offsetY: mode === "move" ? pointerYInTimeline - blockTop : 0,
        currentTop: blockTop,
        currentDuration: durationMinutes,
        originalTop: blockTop,
        originalDuration: durationMinutes,
        conflictTitle: null,
      });
      setDragNotice(null);
    },
    [blocks, timelineStartHour]
  );

  const queueBoardMove = useCallback(
    (event: React.PointerEvent<HTMLElement>, blockId: string) => {
      if (!timelineRef.current) return;
      const block = blocks.find((item) => item.id === blockId);
      if (!block || block.kind === "calendar") return;

      const target = event.target as HTMLElement;
      if (target.closest('[data-resize-handle="true"]')) {
        return;
      }

      const rect = timelineRef.current.getBoundingClientRect();
      const blockTop = minuteOffset(block.start_time, timelineStartHour) * PX_PER_MINUTE;
      const pointerYInTimeline = event.clientY - rect.top + timelineRef.current.scrollTop;
      pendingBoardMoveRef.current = {
        blockId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        offsetY: pointerYInTimeline - blockTop,
        originalTop: blockTop,
        originalDuration: blockDurationMinutes(block.start_time, block.end_time),
      };
      setSelectedNoteId(blockId);
      setDragNotice(null);
    },
    [blocks, timelineStartHour]
  );

  useEffect(() => {
    const beginPendingMove = (event: PointerEvent) => {
      const pending = pendingBoardMoveRef.current;
      if (!pending || boardInteractionRef.current) return;
      const distance = Math.hypot(event.clientX - pending.startClientX, event.clientY - pending.startClientY);
      if (distance < 6) return;
      pendingBoardMoveRef.current = null;
      setBoardInteraction({
        blockId: pending.blockId,
        mode: "move",
        offsetY: pending.offsetY,
        currentTop: pending.originalTop,
        currentDuration: pending.originalDuration,
        originalTop: pending.originalTop,
        originalDuration: pending.originalDuration,
        conflictTitle: null,
      });
    };

    const clearPendingMove = () => {
      if (!boardInteractionRef.current) {
        pendingBoardMoveRef.current = null;
      }
    };

    document.addEventListener("pointermove", beginPendingMove);
    document.addEventListener("pointerup", clearPendingMove);
    document.addEventListener("pointercancel", clearPendingMove);

    return () => {
      document.removeEventListener("pointermove", beginPendingMove);
      document.removeEventListener("pointerup", clearPendingMove);
      document.removeEventListener("pointercancel", clearPendingMove);
    };
  }, []);

  useEffect(() => {
    if (!boardInteraction) return;

    // rAF-throttled state update so 120Hz pointermove events don't trigger
    // 120 React renders/sec. We capture the latest pointer event each
    // frame and only commit one setState per frame.
    let pendingFrame: number | null = null;
    let latestEvent: PointerEvent | null = null;

    const flushMove = () => {
      pendingFrame = null;
      const event = latestEvent;
      latestEvent = null;
      if (!event || !timelineRef.current) return;
      const activeInteraction = boardInteractionRef.current;
      if (!activeInteraction) return;
      const block = blocksRef.current.find((item) => item.id === activeInteraction.blockId);
      if (!block) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const pointerYInTimeline = event.clientY - rect.top + timelineRef.current.scrollTop;
      let nextTop = activeInteraction.currentTop;
      let nextDuration = activeInteraction.currentDuration;

      if (activeInteraction.mode === "move") {
        let proposedTop = pointerYInTimeline - activeInteraction.offsetY;
        const maxTop = totalTimelineMinutes * PX_PER_MINUTE - activeInteraction.currentDuration * PX_PER_MINUTE;
        proposedTop = Math.max(0, Math.min(proposedTop, maxTop));
        // Snap to DRAG_SNAP_MINUTES *during* the drag so the preview
        // line and the time label exactly match where the block will
        // land on release. Tracking pixel-perfect during drag and
        // snapping only on release made the tile jump 1–3px when the
        // user let go, which read as "the line is a bit off".
        const snappedMinutes =
          Math.round(proposedTop / PX_PER_MINUTE / DRAG_SNAP_MINUTES) *
          DRAG_SNAP_MINUTES;
        nextTop = snappedMinutes * PX_PER_MINUTE;
      } else {
        // Resize: snap during drag too so the bottom edge of the
        // tile lines up with the dashed preview line.
        let proposedDuration =
          (pointerYInTimeline - activeInteraction.currentTop) / PX_PER_MINUTE;
        const maxDuration = totalTimelineMinutes - activeInteraction.currentTop / PX_PER_MINUTE;
        proposedDuration = Math.max(15, Math.min(proposedDuration, maxDuration));
        nextDuration = Math.max(
          15,
          Math.round(proposedDuration / DRAG_SNAP_MINUTES) * DRAG_SNAP_MINUTES,
        );
      }

      const { startISO, endISO } = buildPreviewTimes(block, nextTop, nextDuration);
      const conflictTitle = findConflictTitle(block.id, startISO, endISO);

      setBoardInteraction((current) =>
        current
          ? {
              ...current,
              currentTop: nextTop,
              currentDuration: nextDuration,
              conflictTitle,
            }
          : null
      );
    };

    const handleMove = (event: PointerEvent) => {
      latestEvent = event;
      if (pendingFrame === null) {
        pendingFrame = requestAnimationFrame(flushMove);
      }
    };

    const handleUp = async () => {
      const activeInteraction = boardInteractionRef.current;
      if (!activeInteraction) return;
      // Flag that a drag just completed so onClick doesn't open the popup
      dragJustEndedRef.current = true;
      requestAnimationFrame(() => { dragJustEndedRef.current = false; });
      const block = blocksRef.current.find((item) => item.id === activeInteraction.blockId);
      if (!block) {
        setBoardInteraction(null);
        return;
      }

      // Overlaps are allowed — `computeBoardLayouts` packs concurrent blocks
      // into side-by-side columns automatically. Surface the overlap as an
      // informational notice rather than a hard rejection so the user can
      // intentionally schedule parallel blocks.
      if (activeInteraction.conflictTitle) {
        setDragNotice(`"${displayBlockTitle(block)}" runs alongside "${activeInteraction.conflictTitle}".`);
      }

      const unchanged =
        Math.abs(activeInteraction.currentTop - activeInteraction.originalTop) < 1 &&
        activeInteraction.currentDuration === activeInteraction.originalDuration;
      if (unchanged) {
        setBoardInteraction(null);
        return;
      }

      // Snap to DRAG_SNAP_MINUTES on commit — the cursor was tracked
      // pixel-perfect during drag, but blocks should land on clean
      // 5-min slots in the DB.
      const snappedTopMinutes =
        Math.round(activeInteraction.currentTop / PX_PER_MINUTE / DRAG_SNAP_MINUTES) *
        DRAG_SNAP_MINUTES;
      const snappedTop = snappedTopMinutes * PX_PER_MINUTE;
      const snappedDuration =
        activeInteraction.mode === "resize"
          ? Math.max(
              15,
              Math.round(activeInteraction.currentDuration / DRAG_SNAP_MINUTES) *
                DRAG_SNAP_MINUTES,
            )
          : activeInteraction.currentDuration;

      const { startISO: nextStartISO, endISO: nextEndISO } = buildPreviewTimes(
        block,
        snappedTop,
        snappedDuration
      );

      setBlocks((current) =>
        current
          .map((item) =>
            item.id === block.id
              ? { ...item, start_time: nextStartISO, end_time: nextEndISO }
              : item
          )
          .sort((a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime())
      );
      setBoardInteraction(null);
      setDragNotice(
        activeInteraction.mode === "resize"
          ? `Resized "${displayBlockTitle(block)}" to ${formatTimeRange(nextStartISO, nextEndISO)}.`
          : `Moved "${displayBlockTitle(block)}" to ${formatTimeRange(nextStartISO, nextEndISO)}.`
      );

      // Optimistic update: reflect the change locally without a full reload
      setBlocks((prev) =>
        prev.map((b) =>
          b.id === block.id
            ? { ...b, start_time: nextStartISO, end_time: nextEndISO }
            : b
        ).sort((a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime())
      );

      try {
        await updateScheduleBlock(block.id, {
          start_time: nextStartISO,
          end_time: nextEndISO,
        });
        // No background reload here. The backend's `GET /schedule?date=YYYY-MM-DD`
        // filters by UTC day, so a block whose new local time crosses
        // midnight UTC (common in negative-offset timezones for evening
        // edits) drops out of the response and the dragged tile would
        // visually disappear. The optimistic update above already has
        // the authoritative new times — trust it. Fresh server state
        // will reconcile on the next date change or genuine refresh.
        suppressNextScheduleSelfRefreshRef.current = true;
        notifyScheduleChange();
      } catch {
        setDragNotice("Could not save that move. The schedule was refreshed back to server truth.");
        await loadDataAndClearDrag();
      }
    };

    const clearDrag = () => setBoardInteraction(null);

    document.addEventListener("pointermove", handleMove);
    document.addEventListener("pointerup", handleUp);
    document.addEventListener("pointercancel", clearDrag);

    return () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerup", handleUp);
      document.removeEventListener("pointercancel", clearDrag);
      if (pendingFrame !== null) {
        cancelAnimationFrame(pendingFrame);
      }
    };
  }, [boardInteraction, buildPreviewTimes, findConflictTitle, loadDataAndClearDrag, notifyScheduleChange]);

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-6 pb-24">
        <div className="h-5 w-40 animate-pulse rounded bg-muted/10" />
        <div className="mt-4 h-32 animate-pulse rounded-[28px] bg-muted/5" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl min-w-0 px-4 py-6 pb-24">
      <AnimatePresence>
        {(scheduleUndoNotice || errorMsg || dragNotice) && (
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            className="pointer-events-none fixed inset-x-0 top-4 z-[120] flex justify-center px-4"
          >
            <div
              className={`pointer-events-auto flex w-full max-w-xl items-center justify-between gap-3 rounded-2xl border px-4 py-3 shadow-lg backdrop-blur ${
                scheduleUndoNotice
                  ? "border-blue/20 bg-white/95"
                  : errorMsg
                    ? "border-amber/25 bg-sand-pale/95"
                    : "border-blue/20 bg-white/95"
              }`}
              data-testid={scheduleUndoNotice ? "schedule-undo-toast" : "schedule-top-toast"}
            >
              <p className="min-w-0 text-sm text-text">
                {scheduleUndoNotice ? (
                  <>
                    Marked <span className="font-semibold">{scheduleUndoNotice.title}</span> complete.
                  </>
                ) : (
                  errorMsg || dragNotice
                )}
              </p>
              {scheduleUndoNotice ? (
                <button
                  type="button"
                  onClick={() => void handleUndoSelectedBlockCompletion()}
                  className="shrink-0 rounded-full bg-blue/10 px-3 py-1 text-xs font-semibold text-blue transition-colors hover:bg-blue/15"
                >
                  Undo
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setErrorMsg(null);
                    setDragNotice(null);
                  }}
                  className="shrink-0 rounded-full px-2 py-1 text-[11px] font-semibold text-muted transition-colors hover:bg-bg hover:text-text"
                >
                  Close
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={`grid gap-4 xl:items-start ${
          planRightRailCollapsed
            ? "xl:grid-cols-[minmax(0,1fr)_56px]"
            : "xl:grid-cols-[minmax(0,1fr)_320px]"
        }`}
      >
        <PlanBoard>

              <div className="px-1 pb-1 xl:hidden">
                {/* Day strip with inline < > arrows. The "Back to today"
                    pill swaps sides based on which direction the user
                    is away from today, so it visually matches the
                    swipe-back gesture. On xl+ this is replaced by the
                    right-rail compact pager. */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setDate((current) => shiftDate(current, -1))}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-text-light transition-colors hover:bg-secondary"
                    aria-label="Previous day"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  {todayDirection === "left" && (
                    <button
                      onClick={() => setDate(todayStr())}
                      className="h-9 shrink-0 rounded-full border border-border bg-bg px-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-light transition-colors hover:bg-secondary"
                    >
                      Today
                    </button>
                  )}
                  <div className="flex flex-1 gap-1.5 overflow-x-auto">
                    {visibleDayStrip.map((day) => {
                      const active = day.value === date;
                      const today = day.value === todayStr();
                      return (
                        <button
                          key={day.value}
                          onClick={() => setDate(day.value)}
                          className={`flex min-w-[52px] flex-1 flex-col items-center rounded-2xl border px-2 py-2 transition-colors ${
                            active
                              ? "border-forest bg-forest-med text-white"
                              : "border-border bg-bg text-text-light hover:bg-secondary"
                          }`}
                        >
                          <span className={`text-[10px] font-semibold uppercase tracking-[0.08em] ${active ? "text-white/80" : "text-muted"}`}>
                            {today ? "Today" : day.label}
                          </span>
                          <span className="text-sm font-semibold">{day.day}</span>
                        </button>
                      );
                    })}
                  </div>
                  {todayDirection === "right" && (
                    <button
                      onClick={() => setDate(todayStr())}
                      className="h-9 shrink-0 rounded-full border border-border bg-bg px-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-light transition-colors hover:bg-secondary"
                    >
                      Today
                    </button>
                  )}
                  <button
                    onClick={() => setDate((current) => shiftDate(current, 1))}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-text-light transition-colors hover:bg-secondary"
                    aria-label="Next day"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="mt-4">
                <div className="px-1 py-5">
                  {/* NOW card on the left, day-actions on the right.
                      The Wednesday-header strip was redundant — the
                      day-strip above already names the day. The NOW
                      card now also shows live progress through the
                      currently-active block when there is one. */}
                  <div
                    className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
                  >
                    <div
                      className="flex min-w-0 flex-1 flex-col gap-1 border-l-2 border-forest/20 pl-4"
                      data-testid="schedule-now-card"
                      data-commitment-level={boardRhythm[0].block?.commitment_level ?? "none"}
                    >
                      <p className={`text-[10px] font-semibold uppercase tracking-[0.16em] ${boardRhythm[0].labelTone}`}>
                        {boardRhythm[0].label}
                      </p>
                      <p className="mt-2 text-sm font-semibold text-text">
                        {boardRhythm[0].title}
                      </p>
                      <p className="mt-1 text-[11px] leading-relaxed text-text-light">
                        {boardRhythm[0].detail}
                      </p>
                      {boardRhythm[0].block && (
                        <NowBlockProgress
                          start={boardRhythm[0].block.start_time}
                          end={boardRhythm[0].block.end_time}
                        />
                      )}
                      {/* Parallel blocks running at the same time — list
                          them under the primary NOW so the user sees
                          everything that's currently in flight, not just
                          the first match. */}
                      {boardRhythm[0].block && allActiveNowBlocks.length > 1 && (
                        <div
                          className="mt-3 border-t border-border/60 pt-3"
                          data-testid="schedule-now-parallel"
                        >
                          <p className="text-[9.5px] font-semibold uppercase tracking-[0.18em] text-muted">
                            Also running
                          </p>
                          <ul className="mt-1.5 flex flex-col gap-2">
                            {allActiveNowBlocks
                              .filter((block) => block.id !== boardRhythm[0].block?.id)
                              .map((block) => (
                                <li
                                  key={block.id}
                                  className="flex flex-col gap-0.5"
                                  data-block-id={block.id}
                                >
                                  <p className="text-[12.5px] font-semibold text-text">
                                    {compactSurfaceLabel(displayBlockTitle(block), 38)}
                                  </p>
                                  <p className="text-[10.5px] text-text-light">
                                    {formatTimeRange(block.start_time, block.end_time)}
                                  </p>
                                  <NowBlockProgress
                                    start={block.start_time}
                                    end={block.end_time}
                                  />
                                </li>
                              ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div
                      className="flex flex-wrap items-center gap-2 md:shrink-0 xl:hidden"
                      data-testid="schedule-board-header-actions"
                    >
                      <button
                        type="button"
                        onClick={() => handleAIPlan()}
                        disabled={aiPlanLoading}
                        aria-describedby={aiPlanLoading ? "schedule-ai-loading-status-copy" : undefined}
                        data-testid="schedule-ai-timebox-button-mobile"
                        className="inline-flex h-9 items-center rounded-full border border-border bg-card/80 px-3 text-xs font-medium text-text-light transition-colors hover:bg-white disabled:opacity-50"
                      >
                        {aiPlanLoading ? "Planning..." : "AI Time Box"}
                      </button>
                      <button
                        type="button"
                        onClick={handleAddTimebox}
                        data-testid="schedule-add-timebox-button-mobile"
                        className="inline-flex h-9 items-center rounded-full border border-border bg-white/90 px-3 text-xs font-medium text-text-light transition-colors hover:bg-white"
                      >
                        Add timebox
                      </button>
                    </div>
                  </div>
                  {aiPlanLoading && aiPlanStatusText && (
                    <div
                      className={`mb-3 rounded-2xl border px-3.5 py-3 xl:hidden ${
                        aiPlanLongRunning
                          ? "border-amber/25 bg-amber/10"
                          : "border-border/70 bg-card/80"
                      }`}
                      data-testid="schedule-ai-loading-status"
                    >
                      <p
                        id="schedule-ai-loading-status-copy"
                        role="status"
                        aria-live="polite"
                        className="text-xs leading-relaxed text-text-light"
                      >
                        {aiPlanStatusText}
                      </p>
                      <button
                        type="button"
                        onClick={handleCancelAIPlan}
                        className="mt-2 rounded-full border border-border bg-white px-3 py-1.5 text-[11px] font-semibold text-text-light transition hover:bg-secondary/60 hover:text-text"
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] font-medium text-muted">
                    {Object.entries(LIFE_AREA_META).map(([key, meta]) => {
                      const isActive = activeBoardLifeAreas.areas.has(key);
                      return (
                        <span
                          key={key}
                          data-testid={`schedule-life-area-indicator-${key}`}
                          data-active={isActive ? "true" : "false"}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 transition-colors ${
                            isActive ? "shadow-sm" : "opacity-35"
                          }`}
                          style={
                            isActive
                              ? {
                                  backgroundColor: hexWithAlpha(meta.color, "1f"),
                                  color: meta.color,
                                }
                              : undefined
                          }
                        >
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: meta.color }} />
                          {meta.label}
                        </span>
                      );
                    })}
                  </div>

                  {(interactionPreview?.conflictTitle || carryForwardFocusBlockId) && (
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      {interactionPreview?.conflictTitle && (
                        <span className="rounded-full bg-rose/10 px-3 py-1.5 text-[11px] font-semibold text-rose">
                          Overlaps {interactionPreview.conflictTitle}
                        </span>
                      )}
                      {carryForwardFocusBlockId && carryForwardFocusBlockId !== selectedNoteId && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedNoteId(carryForwardFocusBlockId);
                            // UAT 2026-05-25 (Bug #5): the original
                            // version relied solely on the useEffect at
                            // line 1979 to scroll the matching note
                            // into view when selectedNoteId changed.
                            // That effect only fires on selectedNoteId
                            // transition AND only scrolls the
                            // timelineRef container. In production the
                            // user reported clicking the button was a
                            // visual no-op — most likely because the
                            // matching note element was not inside
                            // timelineRef (e.g. promoted to a Later
                            // section), so querySelector found nothing.
                            // Add a direct fallback using the global
                            // document selector, deferred a frame so
                            // any state-driven layout shift settles
                            // first.
                            const targetId = carryForwardFocusBlockId;
                            requestAnimationFrame(() => {
                              const el = document.querySelector<HTMLElement>(
                                `[data-note-id="${targetId}"]`
                              );
                              el?.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              });
                            });
                          }}
                          data-testid="schedule-focus-return"
                          className="rounded-full bg-sage-pale px-3 py-1.5 text-[11px] font-semibold text-forest-med shadow-sm ring-1 ring-black/5 transition hover:bg-sage/20"
                        >
                          Back to start block
                        </button>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <p className="text-xl font-semibold text-text">Timeline</p>
                    <p
                      className="text-xs font-medium text-muted"
                      data-testid="schedule-board-meta"
                    >
                      {scheduleBoardMeta}
                    </p>
                    {laterBoardSummary && !showAllLaterNotes && (
                      <button
                        type="button"
                        onClick={() => setShowAllLaterNotes(true)}
                        data-testid="schedule-show-later-notes"
                        className="ml-auto rounded-full border border-amber/30 bg-amber/5 px-3 py-1 text-[11px] font-semibold text-amber transition hover:bg-amber/10"
                      >
                        Show {laterBoardSummary.count} later note{laterBoardSummary.count === 1 ? "" : "s"}
                      </button>
                    )}
                  </div>
                <div
                  ref={timelineRef}
                  className="relative mt-5 overflow-hidden cursor-crosshair"
                  data-testid="schedule-blocks"
                  onDoubleClick={(e) => {
                    // Only trigger on empty space, not on blocks
                    const target = e.target as HTMLElement;
                    if (target.closest('[data-testid="schedule-note"]')) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const clickY = e.clientY - rect.top + e.currentTarget.scrollTop;
                    const minutesFromStart = clickY / PX_PER_MINUTE;
                    openCreateBlockAt(minutesFromStart);
                  }}
                >
                  {blocks.length === 0 ? (
                    <div
                      className="flex h-48 items-center justify-center text-center"
                      data-testid="plan-empty-state"
                    >
                      <div>
                        <p className="text-sm font-medium text-text">
                          Nothing scheduled for this day.
                        </p>
                        <p className="mt-1 text-xs text-muted">
                          Want me to shape it from your active goals?
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            // This is a preview-and-confirm flow. Do not mark
                            // a backend action as executed until the user saves
                            // the AI Time Box draft to their day.
                            void handleAIPlan();
                          }}
                          disabled={aiPlanLoading}
                          data-testid="plan-empty-state-cta"
                          className="mt-3 inline-flex h-9 items-center rounded-full bg-forest-med px-4 text-xs font-medium text-white transition-colors hover:bg-forest disabled:opacity-60"
                        >
                          {aiPlanLoading ? "Planning…" : "Plan my day"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="relative" style={{ height: `${totalTimelineMinutes * PX_PER_MINUTE}px` }}>
                      {Array.from({ length: timelineEndHour - timelineStartHour + 1 }, (_, index) => {
                        const hour = timelineStartHour + index;
                        return (
                          <div
                            key={hour}
                            className="absolute inset-x-0 flex items-start gap-4"
                            style={{ top: `${(hour - timelineStartHour) * 60 * PX_PER_MINUTE}px` }}
                          >
                            <div className="w-10 text-right text-xs font-medium text-muted">
                              {hour > 12 ? `${hour - 12}pm` : hour === 12 ? "12pm" : `${hour}am`}
                            </div>
                            <div className="h-px flex-1 bg-border/80" />
                          </div>
                        );
                      })}

                      <div className="absolute inset-y-0 left-16 w-px bg-border/40" />

                      {selectedSlotFrame && (
                        <div
                          className={`absolute left-20 right-2 z-[5] rounded-[28px] border border-dashed ${
                            selectedSlotFrame.conflict
                              ? "border-rose/60 bg-rose/10"
                              : selectedSlotFrame.active
                                ? "border-blue/45 bg-blue/10"
                                : "border-forest/25 bg-forest/5"
                          }`}
                          style={{
                            top: `${selectedSlotFrame.top}px`,
                            height: `${selectedSlotFrame.height}px`,
                          }}
                          data-testid="schedule-slot-preview"
                        />
                      )}

                      {boardBlocks.map((block) => {
                        const activeInteraction =
                          boardInteraction?.blockId === block.id ? boardInteraction : null;
                        const isDraggingThis = Boolean(activeInteraction);
                        const isSelected = selectedNoteId === block.id;
                        const layout = boardLayouts[block.id] ?? { column: 0, count: 1 };
                        const columnCount = Math.max(layout.count, 1);
                        const top = activeInteraction
                          ? activeInteraction.currentTop
                          : minuteOffset(block.start_time, timelineStartHour) * PX_PER_MINUTE;
                        const durationMinutes = activeInteraction
                          ? activeInteraction.currentDuration
                          : blockDurationMinutes(block.start_time, block.end_time);
                        const height = Math.max(30, durationMinutes * PX_PER_MINUTE);
                        const isCompact = height < 68;
                        const isMicro = height < 46;
                        const canDrag = block.kind !== "calendar";
                        // Calendar blocks reflect external Google truth, so
                        // drag/resize stays off — but the user still needs
                        // to be able to open the block to delete a stale
                        // duplicate or a meeting that should not appear on
                        // their plan. The editor modal honors the read-only
                        // semantics for calendar blocks while keeping the
                        // delete control wired up.
                        const canEdit = true;
                        const isInteractive = canDrag;
                        const blockMatchesFocusedGoal = matchesGoalFocusTarget(block, goalFocusTarget);
                        const isCarryForwardFocusBlock = Boolean(
                          !block.is_completed &&
                            (blockMatchesFocusedGoal ||
                              (carryForwardFocusBlockId
                              ? block.id === carryForwardFocusBlockId
                              : carryForwardSummary &&
                                matchesCarryForwardFocusTitle(displayBlockTitle(block), carryForwardSummary)))
                        );
                        const blockFocusBadgeLabel = blockMatchesFocusedGoal
                          ? "Goal restart"
                          : carryForwardFocusBadgeLabel;
                        const previewRange = isDraggingThis
                          ? buildPreviewTimes(block, top, durationMinutes)
                          : null;
                        const timeLabel = previewRange
                          ? formatTimeRange(previewRange.startISO, previewRange.endISO)
                          : formatTimeRange(block.start_time, block.end_time);
                        const showInlineTimeLabel = !isMicro && columnCount === 1;
                        const isPulsing = savePulseBlockId === block.id;
                        // Source-block anchor while the editorial modal
                        // is open on this block (NOT during create) — a
                        // terracotta ring + slight lift so the user
                        // never loses track of which block is being
                        // edited. Pulse keyframe takes precedence.
                        const isAnchored =
                          !isPulsing &&
                          !creatingNewBlock &&
                          editingTimelineBlockId === block.id;
                        // Protected ("must_do") blocks have a golden
                        // inset frame applied via Tailwind's
                        // box-shadow utility. Our animate prop
                        // overrides inline box-shadow, so we have to
                        // re-include the inset shadow in every animate
                        // target — otherwise the frame vanishes.
                        const isProtected =
                          (block.priority ?? 0) < 2 &&
                          block.commitment_level === "must_do";
                        const protectedShadow = isProtected
                          ? "inset 0 0 0 2px #B8893B, 0 0 10px rgba(184,137,59,0.18)"
                          : "";
                        const restShadow = protectedShadow || "0 0 0 0 rgba(47,90,47,0)";
                        const anchorShadow = protectedShadow
                          ? `${protectedShadow}, 0 0 0 2px #C97A3D, 0 12px 32px -8px rgba(201,122,61,0.55)`
                          : "0 0 0 2px #C97A3D, 0 12px 32px -8px rgba(201,122,61,0.55)";
                        const pulseShadow = (outerSpec: string): string =>
                          protectedShadow ? `${protectedShadow}, ${outerSpec}` : outerSpec;
                        const showSecondaryTimeLabel = columnCount > 1 && height >= 56;
                        const blockTitle = displayBlockTitle(block);
                        const baseToneClass =
                          (block.priority ?? 0) >= 3
                            ? "border-2 border-rose/50 bg-rose/5"
                            : (block.priority ?? 0) >= 2
                              ? "border-2 border-amber/50 bg-amber/5"
                              : `border ${blockTone(block)}`;
                        const commitmentClasses = blockCommitmentClasses(block);
                        const toneStyle =
                          (block.priority ?? 0) < 2 && !block.is_completed
                            ? blockToneStyle(block)
                            : undefined;
                        return (
                          <motion.div
                            key={block.id}
                            // Drop Framer's `layout` observer here — it
                            // tries to spring-animate every `top` delta
                            // we drive from state, fighting the drag and
                            // adding latency. Pure style updates are
                            // smoother for absolute-positioned blocks.
                            className={`absolute overflow-hidden rounded-[6px] ${baseToneClass} ${commitmentClasses} ${boardInteraction && !isDraggingThis ? "opacity-40" : ""} ${
                              isInteractive ? "cursor-grab active:cursor-grabbing" : ""
                            } ${activeInteraction?.conflictTitle ? "ring-2 ring-rose/40" : ""} ${
                              isDraggingThis && !activeInteraction?.conflictTitle ? "ring-2 ring-blue/30 shadow-lg" : ""
                            } ${isSelected && !isDraggingThis ? "ring-1 ring-forest/25" : ""
                            }`}
                            onPointerDown={
                              isInteractive
                                ? (event) => queueBoardMove(event, block.id)
                                : undefined
                            }
                        onClick={() => {
                          if (dragJustEndedRef.current) return;
                          setSelectedNoteId(block.id);
                          if (canEdit && !block.is_completed) openTimelineBlockEdit(block);
                        }}
                            style={{
                              top: `${top}px`,
                              height: `${height}px`,
                              left: boardColumnLeftCss(layout.column, columnCount),
                              width: boardColumnWidthCss(columnCount),
                              zIndex: isDraggingThis ? 40 : 10 + Math.max(0, layout.count - layout.column),
                              touchAction: isInteractive ? "none" : "auto",
                              ...(toneStyle ?? {}),
                            }}
                            data-testid="schedule-note"
                            data-block-id={block.id}
                            data-reveal-id={block.id}
                            data-note-id={block.id}
                            data-life-area={blockLifeArea(block) ?? "unlinked"}
                            data-commitment-level={block.commitment_level ?? "planned"}
                            data-draggable={isInteractive ? "true" : "false"}
                            data-selected={isSelected ? "true" : "false"}
                            animate={
                              isPulsing
                                ? {
                                    // savedPulse: rise to terracotta →
                                    // peak (green + lift + scale) →
                                    // hold → single smooth descent.
                                    boxShadow: [
                                      pulseShadow("0 0 0 2px rgba(201,122,61,0.95), 0 12px 32px -8px rgba(201,122,61,0.55)"),
                                      pulseShadow("0 0 0 3px #2F5A2F, 0 18px 40px -6px rgba(47,90,47,0.65)"),
                                      pulseShadow("0 0 0 3px #2F5A2F, 0 18px 40px -6px rgba(47,90,47,0.6)"),
                                      restShadow,
                                    ],
                                    y: [-1, -3, -3, 0],
                                    scale: [1.015, 1.05, 1.05, 1],
                                  }
                                : isAnchored
                                  ? {
                                      boxShadow: anchorShadow,
                                      y: -1,
                                      scale: 1.015,
                                    }
                                  : { boxShadow: restShadow, y: 0, scale: 1 }
                            }
                            transition={
                              isPulsing
                                ? {
                                    duration: 1.05,
                                    times: [0, 0.24, 0.5, 1],
                                    ease: [0.2, 0.8, 0.2, 1],
                                  }
                                : { duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }
                            }
                          >
                            {/* Left rail color stripe */}
                            <div className="absolute inset-y-0 left-0 w-1">
                              <div className="h-full w-full" style={blockRailStyle(block)} />
                            </div>

                            <div className="relative flex h-full flex-col justify-center gap-0.5 pl-3 pr-2">
                              <div className="flex items-center gap-1.5">
                                {showInlineTimeLabel && (
                                  <span className="shrink-0 whitespace-nowrap text-[10px] font-medium text-text-light/70">
                                    {timeLabel}
                                  </span>
                                )}
                                <span
                                  className={`min-w-0 truncate font-medium text-text ${
                                    isMicro || columnCount > 1 ? "text-[11px]" : "text-xs"
                                  }`}
                                >
                                  {blockTitle}
                                  {(block.duration_days ?? 1) > 1 && (
                                    <span className="ml-1 text-[9px] font-normal text-muted">
                                      ({block.duration_days}d)
                                    </span>
                                  )}
                                </span>
                                {isCarryForwardFocusBlock && blockFocusBadgeLabel && (
                                  <span className="shrink-0 rounded bg-forest-med px-1.5 py-0.5 text-[9px] font-semibold text-white">
                                    {blockFocusBadgeLabel}
                                  </span>
                                )}
                              </div>
                              {showSecondaryTimeLabel && (
                                <span className="truncate text-[10px] font-medium text-text-light/70">
                                  {timeLabel}
                                </span>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}

                      {/* "✓ Saved" toast — floats up from the just-saved block.
                          Rendered outside the block element so the float-up is
                          not clipped by the block's overflow-hidden. */}
                      <AnimatePresence>
                        {savePulseBlockId && (() => {
                          const pulsedBlock = blocks.find((b) => b.id === savePulseBlockId);
                          if (!pulsedBlock) return null;
                          const pulsedTop = minuteOffset(pulsedBlock.start_time, timelineStartHour) * PX_PER_MINUTE;
                          const pulsedLayout = boardLayouts[pulsedBlock.id] ?? { column: 0, count: 1 };
                          const pulsedCount = Math.max(pulsedLayout.count, 1);
                          return (
                            <motion.div
                              key={`save-toast-${savePulseBlockId}`}
                              className="pointer-events-none absolute z-40"
                              style={{
                                top: `${pulsedTop}px`,
                                left: boardColumnLeftCss(pulsedLayout.column, pulsedCount),
                                width: boardColumnWidthCss(pulsedCount),
                              }}
                              initial={{ opacity: 0, y: 0, scale: 0.9 }}
                              animate={{
                                opacity: [0, 1, 1, 0],
                                y: [0, -32, -32, -44],
                                scale: [0.9, 1, 1, 1],
                              }}
                              exit={{ opacity: 0 }}
                              transition={{
                                duration: 1.1,
                                times: [0, 0.22, 0.64, 1],
                                ease: [0.2, 0.8, 0.2, 1],
                              }}
                            >
                              <div className="flex justify-center">
                                <span
                                  className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10.5px] font-semibold uppercase text-white shadow-md"
                                  style={{ background: "#2F5A2F", letterSpacing: "1.2px" }}
                                >
                                  <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                                    <path d="M2.5 7.5 L6 11 L11.5 4" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                  Saved
                                </span>
                              </div>
                            </motion.div>
                          );
                        })()}
                      </AnimatePresence>

                      {interactionPreview && (
                        <div
                          className="absolute left-0 right-0 z-30 flex items-center pointer-events-none"
                          style={{ top: `${interactionPreview.lineTop}px` }}
                        >
                          <div className="w-10 text-right">
                            <span
                              className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                                interactionPreview.conflictTitle
                                  ? "bg-rose/10 text-rose"
                                  : "bg-forest/10 text-forest"
                              }`}
                            >
                              {interactionPreview.mode === "resize"
                                ? `${interactionPreview.label} · ${interactionPreview.durationMinutes}m`
                                : interactionPreview.label}
                            </span>
                          </div>
                          <div
                            className={`ml-4 flex-1 border-t-2 border-dashed ${
                              interactionPreview.conflictTitle ? "border-rose/70" : "border-forest/70"
                            }`}
                          />
                        </div>
                      )}

                      {isToday && nowIndicatorTop != null && (
                        <div
                          className="absolute left-16 right-0 z-20 flex items-center pointer-events-none"
                          style={{ top: `${nowIndicatorTop}px` }}
                        >
                          <div className="h-2.5 w-2.5 -ml-1.5 rounded-full bg-rose shadow-[0_0_0_6px_rgba(232,91,91,0.08)]" />
                          <div className="flex-1 border-t-2 border-rose/80" />
                        </div>
                      )}
                    </div>
                  )}

                </div>
                </div>
              </div>

              {/* Precision editor removed — click any block to edit inline */}
        </PlanBoard>

        <PlanRightRail
          date={date}
          todayValue={todayStr()}
          threeDayStrip={threeDayStrip}
          onDateChange={(next) => setDate(next)}
          onShiftDay={(delta) => setDate((current) => shiftDate(current, delta))}
          onAIPlan={() => handleAIPlan()}
          aiPlanLoading={aiPlanLoading}
          aiPlanStatusText={aiPlanStatusText}
          aiPlanLongRunning={aiPlanLongRunning}
          onCancelAIPlan={handleCancelAIPlan}
          onAddTimebox={handleAddTimebox}
          collapsed={planRightRailCollapsed}
          onToggleCollapsed={togglePlanRightRail}
          sideContextChildren={(<>
              {hasPlanningContext && (
                <section
                  className="border-b border-border/60 pb-5"
                  data-testid="schedule-planning-context-card"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">
                        Planning context
                      </p>
                      <p className="mt-1 text-sm text-text-light">
                        Keep the start block visible. Open deeper cues only when you need them.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {hiddenPlanningCueCount > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowPlanningContextDetails((current) => !current)}
                          className="rounded-full border border-border/70 px-3 py-1.5 text-[11px] font-semibold text-text-light transition hover:border-forest/30 hover:text-text"
                        >
                          {showPlanningContextDetails ? "Hide details" : "Show cue"}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    {(displayCarryForwardSummary || plannedFocusForCurrentDate) && (
                      <div
                        className="rounded-[20px] border border-blue/15 bg-blue/5 px-3.5 py-3"
                        data-testid="schedule-carry-forward-context"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-blue">
                              {scheduleCarryForwardPanelTitle}
                            </p>
                            <p className="mt-2 text-xs leading-relaxed text-text-light">
                              {displayCarryForwardSummary
                                ? scheduleCarryForwardPanelHint || buildCarryForwardMessage(displayCarryForwardSummary)
                                : sanitizeSurfaceTitle(scheduleCarryForwardPanelBody || "")}
                            </p>
                            {!displayCarryForwardSummary && scheduleCarryForwardPanelHint && (
                              <p className="mt-2 text-[11px] font-medium leading-relaxed text-forest-med">
                                {sanitizeSurfaceTitle(scheduleCarryForwardPanelHint)}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (displayCarryForwardSummary) {
                                clearCarryForwardSummary();
                                return;
                              }
                              clearPersistedPlannedFocus();
                            }}
                            className="rounded-full px-2 py-1 text-[10px] font-semibold text-muted transition-colors hover:bg-card/70 hover:text-text"
                          >
                            Hide
                          </button>
                        </div>
                        {displayCarryForwardSummary && (
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {displayCarryForwardSummary.tomorrow_count > 0 && (
                              <span className="rounded-full bg-card px-2.5 py-1 text-[10px] font-semibold text-blue">
                                Today {displayCarryForwardSummary.tomorrow_count}
                              </span>
                            )}
                            {displayCarryForwardSummary.later_count > 0 && (
                              <span className="rounded-full bg-card px-2.5 py-1 text-[10px] font-semibold text-text-light">
                                Later {displayCarryForwardSummary.later_count}
                              </span>
                            )}
                            {displayCarryForwardSummary.made_smaller_count > 0 && (
                              <span className="rounded-full bg-card px-2.5 py-1 text-[10px] font-semibold text-forest-med">
                                Smaller {displayCarryForwardSummary.made_smaller_count}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {showPlanningContextDetails && executionPatternHint && (
                      <div
                        className="rounded-[20px] border border-forest/15 bg-sage-pale/35 px-3.5 py-3"
                        data-testid="schedule-execution-pattern-hint"
                      >
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-forest-med">
                          Execution pattern
                        </p>
                        <p className="mt-1 text-[11px] font-semibold text-text">
                          {sanitizeSurfaceTitle(executionPatternHint.title)}
                        </p>
                        <p className="mt-1 text-[11px] leading-relaxed text-text-light">
                          {sanitizeSurfaceTitle(executionPatternHint.message)}
                        </p>
                        {executionPatternAction && (
                          <p className="mt-2 text-[11px] font-medium leading-relaxed text-forest-med">
                            Best next move: {sanitizeSurfaceTitle(executionPatternAction)}
                          </p>
                        )}
                      </div>
                    )}

                    {showPlanningContextDetails && goalDriftSummaryContext && (
                      <div
                        className="space-y-2"
                        data-testid="schedule-goal-drift-hint"
                      >
                        {/* Compact one-line header. The headline +
                            primary_goal mini-card + top-level message
                            used to live above this — folded into each
                            goal card below to remove the duplicate
                            rendering of the same goal in two places. */}
                        <div className="flex items-center justify-between gap-3 px-1">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-blue">
                            Goal trajectory
                            {goalDriftSummaryContext.drifting_goal_count !== undefined && (
                              <>
                                <span className="text-muted"> · </span>
                                <span className="text-text-light">
                                  {goalDriftSummaryContext.drifting_goal_count} drifting
                                </span>
                                {goalDriftSummaryContext.steady_goal_count !== undefined && (
                                  <>
                                    <span className="text-muted"> · </span>
                                    <span className="text-text-light">
                                      {goalDriftSummaryContext.steady_goal_count} steady
                                    </span>
                                  </>
                                )}
                              </>
                            )}
                          </p>
                          {planningFocusGoalIds.size > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                clearPlanningFocusGoals();
                                clearGoalFocusTarget();
                              }}
                              className="text-[10px] font-semibold uppercase tracking-[0.14em] text-blue/80 transition-colors hover:text-blue"
                            >
                              Clear ({planningFocusGoalIds.size})
                            </button>
                          )}
                        </div>

                        {goalDriftRankedGoals.length === 0 && (
                          <p className="rounded-[20px] border border-border/70 bg-bg/70 px-3.5 py-3 text-[11px] leading-relaxed text-muted">
                            {sanitizeSurfaceTitle(goalDriftSummaryContext.message)}
                          </p>
                        )}

                        {goalDriftRankedGoals.length >= 1 && (
                          <div
                            className="space-y-2"
                            data-testid="schedule-goal-drift-ranked-list"
                          >
                            {goalDriftRankedGoals.map((goal) => {
                              const inMultiSelect = planningFocusGoalIds.has(goal.id);
                              const isActiveGoalRecovery =
                                inMultiSelect ||
                                goalFocusTarget?.goalId === goal.id ||
                                (!goalFocusTarget &&
                                  planningFocusGoalIds.size === 0 &&
                                  goalDriftPrimaryGoal?.id === goal.id);
                              const pct = Math.round((goal.progress_pct ?? 0) * 100);
                              // Trust backend classification — threshold
                              // lives in goal_drift.py. Fallback to score
                              // only if older payloads omit the flag.
                              const isDrifting =
                                goal.is_drifting ?? (goal.drift_score ?? 0) >= 4;
                              const daysSince = goal.days_since_activity ?? null;
                              const metricsParts: string[] = [];
                              if (goal.open_task_count !== undefined) {
                                metricsParts.push(
                                  `${goal.open_task_count} open`,
                                );
                              }
                              if (
                                goal.recent_rollover_count !== undefined &&
                                goal.recent_rollover_count > 0
                              ) {
                                metricsParts.push(
                                  `${goal.recent_rollover_count} restart${goal.recent_rollover_count === 1 ? "" : "s"}`,
                                );
                              }
                              if (daysSince !== null && daysSince > 0) {
                                metricsParts.push(`${daysSince}d quiet`);
                              }
                              return (
                                <button
                                  key={goal.id}
                                  type="button"
                                  data-testid={`schedule-goal-drift-item-${goal.id}`}
                                  onClick={() => {
                                    togglePlanningFocusGoal(goal.id);
                                    if (planningFocusGoalIds.has(goal.id)) {
                                      if (goalFocusTarget?.goalId === goal.id) {
                                        clearGoalFocusTarget();
                                      }
                                    } else {
                                      setGoalFocusTarget(buildGoalRecoveryTarget(goal));
                                    }
                                  }}
                                  className={`w-full rounded-[20px] border bg-white px-4 py-3 text-left shadow-sm transition-colors ${
                                    inMultiSelect
                                      ? "border-2 border-blue bg-blue/5 ring-1 ring-blue/25"
                                      : isActiveGoalRecovery
                                        ? "border-blue/40"
                                        : "border-border/70 hover:border-blue/25"
                                  }`}
                                >
                                  {/* Status badge + progress row */}
                                  <div className="flex items-center justify-between gap-3">
                                    <span
                                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                                        isDrifting
                                          ? "bg-amber/15 text-amber"
                                          : "bg-sage-pale text-forest-med"
                                      }`}
                                    >
                                      {isDrifting ? "⚠ Drifting" : "✓ Steady"}
                                    </span>
                                    <span className="rounded-full bg-bg px-2 py-0.5 text-[10px] font-semibold text-text-light">
                                      {pct}%
                                    </span>
                                  </div>

                                  {/* Title */}
                                  <p className="mt-2 text-[13px] font-semibold leading-snug text-text">
                                    {sanitizeSurfaceTitle(goal.title)}
                                  </p>

                                  {/* Activity metrics */}
                                  {metricsParts.length > 0 && (
                                    <p className="mt-1 text-[10.5px] text-muted">
                                      {metricsParts.join(" · ")}
                                    </p>
                                  )}

                                  {/* Best next move (per-goal,
                                      schedulable unit — sub_step
                                      when parent has been broken
                                      down by Phase 4) */}
                                  {goal.next_step && (
                                    <div
                                      className="mt-3 rounded-lg bg-sage-pale/40 px-2.5 py-2"
                                      data-testid={`schedule-goal-drift-next-step-${goal.id}`}
                                    >
                                      <p className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-forest-med">
                                        Best next move
                                      </p>
                                      <p className="mt-0.5 text-[11.5px] leading-snug text-text">
                                        ▸ {sanitizeSurfaceTitle(goal.next_step)}
                                      </p>
                                    </div>
                                  )}

                                  {/* Recovery note (drift-only,
                                      AI-suggested rationale) */}
                                  {isDrifting && goal.recovery_note && (
                                    <p className="mt-2 text-[11px] italic leading-relaxed text-text-light">
                                      &ldquo;{sanitizeSurfaceTitle(goal.recovery_note)}&rdquo;
                                    </p>
                                  )}

                                  {/* Toggle indicator */}
                                  <div className="mt-3 flex items-center justify-end">
                                    <span
                                      className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors ${
                                        inMultiSelect
                                          ? "bg-blue/15 text-blue"
                                          : "border border-border bg-white text-text-light"
                                      }`}
                                    >
                                      {inMultiSelect ? "✓ Planning this" : "+ Add to AI plan"}
                                    </span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {carryForwardSummary && hasScheduleCarryForwardAction(carryForwardSummary) && (
                      <p className="text-[11px] leading-relaxed text-muted">
                        Use the board below to place work that was moved later without losing today&apos;s lighter start.
                      </p>
                    )}
                  </div>
                </section>
              )}


              <section
                className="border-b border-border/60 pb-5"
                data-testid="calendar-context-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">
                      Calendar context
                    </p>
                    <p className="mt-1 text-sm text-text-light">
                      {planningOverview?.calendar_status === "connected"
                        ? "Google Calendar is read-only here, so the board can plan around your real commitments."
                        : planningOverview?.calendar_status === "error"
                          ? "Google Calendar is connected, but this day's context could not refresh cleanly."
                          : "Connect Google Calendar to place work around the rest of your day."}
                    </p>
                  </div>
                  {planningOverview?.calendar_status === "connected" && (
                    <span className="rounded-full bg-sage-pale px-3 py-1.5 text-[11px] font-semibold text-forest-med">
                      Connected
                    </span>
                  )}
                </div>

                {(planningOverview?.calendar_status === "disconnected" || planningOverview?.calendar_status === "error") && (
                  <GoogleConnectLink
                    className={`mt-4 inline-flex rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                      calendarContextQuiet
                        ? "border border-forest/20 bg-white/80 text-forest-med hover:bg-sage-pale/70"
                        : "bg-forest-med text-white hover:bg-forest"
                    }`}
                  >
                    {planningOverview?.calendar_status === "error" ? "Reconnect Google Calendar" : "Connect Google Calendar"}
                  </GoogleConnectLink>
                )}

                {calendarEvents.length > 0 ? (
                  <div className="mt-4 space-y-2.5">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">
                      Busy windows
                    </p>
                    {calendarEvents.slice(0, 4).map((event) => (
                      <div key={event.id} className="rounded-[22px] border border-border/70 bg-bg px-3.5 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-text">{event.title}</p>
                            <p className="mt-1 text-[11px] text-muted">
                              {event.is_all_day
                                ? "All day"
                                : `${formatContextTime(event.start)} - ${formatContextTime(event.end)}`}
                            </p>
                          </div>
                          <span className="rounded-full bg-blue/10 px-2.5 py-1 text-[10px] font-semibold text-blue">
                            Fixed
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : planningOverview?.calendar_status === "connected" ? (
                  <div className="mt-4 rounded-[22px] border border-border/70 bg-bg px-3.5 py-3">
                    <p className="text-sm font-medium text-text">No Google events on this day</p>
                    <p className="mt-1 text-[11px] leading-relaxed text-muted">
                      The board can use the full day more flexibly here.
                    </p>
                  </div>
                ) : null}

              </section>

              {/* Planning goals card removed — goal trajectory rail
                  (rendered inside Planning Context above) is the
                  single source of truth for goal cards on Plan. The
                  redundant "Planning goals" list with its own header,
                  Open-goals-workspace button, and add-goal CTA was
                  duplicating the trajectory rail's job. The retroactive
                  checklist + add-goal flow are kept available from
                  the Goals tab. */}
          </>)}
        />
      </div>

      {/* Editorial block-edit / create modal — single surface for both
          flows. Save success triggers the green pulse + "✓ Saved" toast
          on the matching timeline tile after the modal collapses. */}
      {(() => {
        // Create flow: openCreateBlockAt seeds the editingTimeline* form
        // state (start, end, commitment, life area). We forward those
        // straight into the modal as `initial`.
        if (creatingNewBlock) {
          return (
            <BlockEditModal
              open
              initial={{
                title: "",
                description: "",
                startTime: editingTimelineStart,
                endTime: editingTimelineEnd,
                commitmentLevel: editingTimelineCommitmentLevel,
                lifeArea: editingTimelineLifeArea,
                durationDays: editingTimelineDurationDays,
                kind: "task",
                goalTitle: null,
                rationale: null,
                blockId: null,
              }}
              onClose={() => {
                setEditingTimelineBlockId(null);
                setCreatingNewBlock(false);
              }}
              onSave={async (payload: BlockEditPayload) => {
                const dateObj = new Date(`${date}T12:00:00`);
                const [sh, sm] = payload.startTime.split(":").map(Number);
                const [eh, em] = payload.endTime.split(":").map(Number);
                const created: ScheduleBlock[] = [];
                for (let d = 0; d < payload.durationDays; d++) {
                  const dayOffset = new Date(dateObj);
                  dayOffset.setDate(dayOffset.getDate() + d);
                  const dStart = new Date(dayOffset.getFullYear(), dayOffset.getMonth(), dayOffset.getDate(), sh, sm).toISOString();
                  const dEnd = new Date(dayOffset.getFullYear(), dayOffset.getMonth(), dayOffset.getDate(), eh, em).toISOString();
                  const c = hydrateScheduleBlock(
                    await createScheduleBlock({
                      title: payload.title.trim() || "New block",
                      description: payload.description.trim() || undefined,
                      start_time: dStart,
                      end_time: dEnd,
                      kind: "task",
                      commitment_level: payload.commitmentLevel,
                      life_area: payload.lifeArea ?? undefined,
                      duration_days: payload.durationDays,
                    }),
                    goalLifeAreaLookup,
                  );
                  created.push(c);
                }
                setBlocks((prev) => {
                  const next = [...prev, ...created].sort(
                    (a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime(),
                  );
                  blocksRef.current = next;
                  return next;
                });
                suppressNextScheduleSelfRefreshRef.current = true;
                notifyScheduleChange();
                // Pulse the just-created block on today's column. Delay
                // matches the modal's beat→saved (420ms) hold so the
                // pulse fires as the modal begins collapsing.
                const firstId = created[0]?.id;
                if (firstId) setTimeout(() => triggerSavePulse(firstId), 1300);
              }}
            />
          );
        }

        const editing = editingTimelineBlockId
          ? blocks.find((b) => b.id === editingTimelineBlockId) ?? null
          : null;
        if (!editing) {
          return (
            <BlockEditModal
              open={false}
              initial={null}
              onClose={() => setEditingTimelineBlockId(null)}
              onSave={async () => {}}
            />
          );
        }
        const startISO = new Date(editing.start_time);
        const endISO = new Date(editing.end_time);
        const fmtHM = (d: Date) =>
          `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
        return (
          <BlockEditModal
            open
            initial={{
              title: editing.title,
              description: editing.description ?? "",
              startTime: fmtHM(startISO),
              endTime: fmtHM(endISO),
              commitmentLevel: (editing.commitment_level as CommitmentLevel) ?? "planned",
              lifeArea: editing.life_area ?? null,
              durationDays: editing.duration_days ?? 1,
              kind: editing.kind ?? null,
              goalTitle: editing.goal_title ?? null,
              rationale: editing.rationale ?? null,
              blockId: editing.id,
            }}
            onClose={() => setEditingTimelineBlockId(null)}
            onComplete={
              editing.kind === "task" && !editing.is_completed
                ? async () => {
                    await handleCompleteSelectedBlock();
                    setEditingTimelineBlockId(null);
                  }
                : undefined
            }
            completing={completingSelectedBlockId === editing.id}
            onSave={async (payload: BlockEditPayload) => {
              const orig = startISO;
              const [sh, sm] = payload.startTime.split(":").map(Number);
              const [eh, em] = payload.endTime.split(":").map(Number);
              const nextStart = new Date(orig.getFullYear(), orig.getMonth(), orig.getDate(), sh, sm).toISOString();
              const nextEnd = new Date(orig.getFullYear(), orig.getMonth(), orig.getDate(), eh, em).toISOString();
              const updated = hydrateScheduleBlock(
                await updateScheduleBlock(editing.id, {
                  title: payload.title,
                  description: payload.description.trim() || undefined,
                  start_time: nextStart,
                  end_time: nextEnd,
                  commitment_level: payload.commitmentLevel,
                  life_area: payload.lifeArea,
                  duration_days: payload.durationDays,
                }),
                goalLifeAreaLookup,
              );

              // If durationDays > 1, propagate the same info to the
              // following days. We only create blocks for days that
              // don't already have an exact-time-slot block carrying
              // the same title (loose dedupe — if the user has been
              // editing the series we don't pile on duplicates).
              const clones: ScheduleBlock[] = [];
              if (payload.durationDays > 1) {
                const blockTitle = payload.title.trim();
                const existingTitlesByDay = new Map<string, boolean>();
                for (const b of blocksRef.current) {
                  const bs = parseUTCishDate(b.start_time);
                  const dayKey = `${bs.getFullYear()}-${bs.getMonth()}-${bs.getDate()}-${bs.getHours()}-${bs.getMinutes()}`;
                  if (sanitizeSurfaceTitle(b.title).toLowerCase() === blockTitle.toLowerCase()) {
                    existingTitlesByDay.set(dayKey, true);
                  }
                }
                for (let d = 1; d < payload.durationDays; d++) {
                  const cloneDate = new Date(orig);
                  cloneDate.setDate(cloneDate.getDate() + d);
                  const cloneStart = new Date(cloneDate.getFullYear(), cloneDate.getMonth(), cloneDate.getDate(), sh, sm);
                  const cloneEnd = new Date(cloneDate.getFullYear(), cloneDate.getMonth(), cloneDate.getDate(), eh, em);
                  const dayKey = `${cloneStart.getFullYear()}-${cloneStart.getMonth()}-${cloneStart.getDate()}-${sh}-${sm}`;
                  if (existingTitlesByDay.has(dayKey)) continue;
                  const created = hydrateScheduleBlock(
                    await createScheduleBlock({
                      title: payload.title,
                      description: payload.description.trim() || undefined,
                      start_time: cloneStart.toISOString(),
                      end_time: cloneEnd.toISOString(),
                      kind: editing.kind,
                      commitment_level: payload.commitmentLevel,
                      goal_id: editing.goal_id ?? undefined,
                      goal_title: editing.goal_title ?? undefined,
                      life_area: payload.lifeArea ?? undefined,
                      duration_days: payload.durationDays,
                    }),
                    goalLifeAreaLookup,
                  );
                  clones.push(created);
                }
              }

              setBlocks((current) => {
                const merged = current
                  .map((item) => (item.id === editing.id ? updated : item))
                  .concat(clones)
                  .sort(
                    (a, b) =>
                      parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime(),
                  );
                blocksRef.current = merged;
                return merged;
              });
              suppressNextScheduleSelfRefreshRef.current = true;
              notifyScheduleChange();
              setTimeout(() => triggerSavePulse(editing.id), 1300);
            }}
            onDelete={async (blockId) => {
              await deleteScheduleBlock(blockId);
              setBlocks((current) => current.filter((b) => b.id !== blockId));
              notifyScheduleChange();
            }}
          />
        );
      })()}

      <AnimatePresence>
        {aiPlan && (
          <AIPlanDiffSheet
            plan={aiPlan}
            onApply={handleApplyPlan}
            onEditManually={handleEditPlanManually}
            onUpdateBlock={handleUpdateAiPlanBlock}
            onEditExistingBlock={(blockId) => {
              const block = blocksRef.current.find((candidate) => candidate.id === blockId);
              if (block) openTimelineBlockEdit(block);
            }}
            onRetry={handleRetryPlan}
            onSaveAsLater={handleSavePlanAsLater}
            onApplyStarterBlocks={handleApplyStarterBlocks}
            onDismiss={handleDismissAiPlan}
            applying={aiPlanApplying}
            fallbackMutating={aiPlanFallbackLoading}
            commitmentLevel={aiPlanCommitmentLevel}
            onCommitmentChange={setAiPlanCommitmentLevel}
            executionPatternHintFallback={planningOverviewExecutionPatternHint}
            goalDriftSummaryFallback={goalDriftSummaryContext}
          />
        )}
      </AnimatePresence>

      <StepBreakdownDialog
        open={breakdownOpen}
        proposals={breakdownProposals}
        onClose={handleCloseBreakdown}
        onConfirm={handleConfirmBreakdown}
      />
    </div>
  );
}

// Live progress meter for the "NOW" card. Shown only when the user is
// currently inside a scheduled block. Re-ticks every 30s so the bar
// fills and the remaining-minutes label stays accurate without forcing
// the parent to recompute its memoized rhythm summary.
function NowBlockProgress({ start, end }: { start: string; end: string }) {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const startMs = parseUTCishDate(start).getTime();
  const endMs = parseUTCishDate(end).getTime();
  const totalMs = Math.max(0, endMs - startMs);
  const elapsedMs = Math.max(0, Math.min(totalMs, now - startMs));
  const progress = totalMs > 0 ? elapsedMs / totalMs : 0;
  const remainingMin = Math.max(0, Math.ceil((endMs - now) / 60_000));
  const elapsedMin = Math.max(0, Math.floor((now - startMs) / 60_000));

  return (
    <div className="mt-3 flex flex-col gap-1.5">
      <div
        className="h-1 w-full overflow-hidden rounded-full bg-border/50"
        role="progressbar"
        aria-valuenow={Math.round(progress * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full bg-forest-med transition-[width] duration-700 ease-out"
          style={{ width: `${Math.min(100, progress * 100)}%` }}
        />
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
        {remainingMin > 0
          ? `${elapsedMin} min in · ${remainingMin} min left`
          : "wraps now"}
      </p>
    </div>
  );
}
